import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import MenuAddBottomSheet from './menuAddBottomSheet';
import CategoryEditBottomSheet from './categoryEditBottomSheet';
import CallSettingsBottomSheet from './callSettingsBottomSheet';
import StoreSettings from './storeSettings';
import OrderOverview from './orderOverview';
import MenuEditBottomSheet from './menuEditBottomSheet';
import SalesTimeBulkApply from './salesTimeBulkApply';
import SoldOutBulkApply from './soldOutBulkApply';
import ReviewSettings from './reviewSettings';
import MenuPickerBottomSheet from './menuPickerBottomSheet';
import api, { getStoreIdFromToken, getStoreCodeFromToken, apiRequest, authHeaders } from '../../utils/api';
import { convertToCdnUrl } from '../../utils/cdnUtils';
import { syncOKDCMasterData } from '../../utils/okdcApi';
import { syncMetacityMasterData } from '../../utils/metacityApi';
import { syncAllUnionData } from '../../utils/unionApi';
import { useMenuDragReorder } from '../../hooks/useMenuDragReorder';
import wsService from '../../utils/websocket';
import { preloadAdminImages } from '../../utils/imagePreloader';
// 관리자 페이지에서는 장바구니 관련 컴포넌트 제거
// import MenuOptionsBottomSheet from '../menuOptionsBottomSheet';
// import CartDrawer from '../cartDrawer';

// Language icons
import langCnIcon from '../../assets/icons/language/cn.png';
import langJaIcon from '../../assets/icons/language/ja.png';
import langEnIcon from '../../assets/icons/language/en.png';
import langKoIcon from '../../assets/icons/language/ko.png';

// Common icons
import bottomNavIcon from '../../assets/icons/common/bottomNav.png';
import navigationBarIcon from '../../assets/icons/common/navigation_bar.png';
import noticeIcon from '../../assets/icons/common/noticeicon.png';
import refuseIcon from '../../assets/icons/common/refuse_icon.png';

// Admin icons
import callbellIcon from '../../assets/icons/admin/callbell.png';
import storeinfoIcon from '../../assets/icons/admin/storeinfo.png';
import noticeOnIcon from '../../assets/icons/admin/notice_on.png';
import noticeOffIcon from '../../assets/icons/admin/notice_off.png';
import blurBackgroundIcon from '../../assets/icons/admin/blur_background.png';
import categoryModifyIcon from '../../assets/icons/admin/category_modify_icon.png';

// Review icons
import liveStatusNaverIcon from '../../assets/icons/review/live_status_naver.png';

// Menutag icons
import menutagKoreanPopular from '../../assets/icons/menutag/korean/popular.png';
import menutagKoreanRecommend from '../../assets/icons/menutag/korean/recommend.png';
import menutagKoreanNew from '../../assets/icons/menutag/korean/new.png';
import menutagEnglishPopular from '../../assets/icons/menutag/english/popular.png';
import menutagEnglishRecommend from '../../assets/icons/menutag/english/recommend.png';
import menutagEnglishNew from '../../assets/icons/menutag/english/new.png';
import menutagChinaPopular from '../../assets/icons/menutag/china/popular.png';
import menutagChinaRecommend from '../../assets/icons/menutag/china/recommend.png';
import menutagChinaNew from '../../assets/icons/menutag/china/new.png';
import menutagJapanPopular from '../../assets/icons/menutag/japan/popular.png';
import menutagJapanRecommend from '../../assets/icons/menutag/japan/recommend.png';
import menutagJapanNew from '../../assets/icons/menutag/japan/new.png';

const menutagMap = {
  korean: { popular: menutagKoreanPopular, recommend: menutagKoreanRecommend, new: menutagKoreanNew },
  english: { popular: menutagEnglishPopular, recommend: menutagEnglishRecommend, new: menutagEnglishNew },
  china: { popular: menutagChinaPopular, recommend: menutagChinaRecommend, new: menutagChinaNew },
  japan: { popular: menutagJapanPopular, recommend: menutagJapanRecommend, new: menutagJapanNew },
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  // 관리자 페이지 아이콘 즉시 프리로드
  useEffect(() => {
    preloadAdminImages();
  }, []);

  // 로그인 확인 및 사용자 정보 로깅
  useEffect(() => {
    // localStorage 또는 sessionStorage에서 인증 정보 확인
    const currentUser = localStorage.getItem('currentUser') || sessionStorage.getItem('currentUser');
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');

    if (!currentUser) {
      navigate('/login');
      return;
    }

    // 사용자 정보 파싱 및 로깅
    try {
      const userInfo = JSON.parse(currentUser);
      console.log('========== 로그인 사용자 정보 ==========');
      console.log('사용자 ID:', userInfo.id);
      console.log('사용자명:', userInfo.username);
      console.log('사업자번호:', userInfo.businessNumber);
      console.log('Store Code:', userInfo.storeCode);
      console.log('이메일:', userInfo.email);
      console.log('저장소 타입:', localStorage.getItem('rememberMe') ? 'localStorage (영구)' : 'sessionStorage (세션)');

      // 토큰 정보 파싱
      if (token) {
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          console.log('========== JWT 토큰 정보 ==========');
          console.log('토큰 Store ID:', payload.id);
          console.log('토큰 Store Code:', payload.storeCode);
          console.log('토큰 만료 시간:', new Date(payload.exp * 1000).toLocaleString());
        } catch (e) {
          console.error('토큰 파싱 오류:', e);
        }
      }

      // API로 현재 store 정보 확인
      console.log('========== Store 상태 정보 ==========');
      console.log('현재 사용 중인 Store ID:', getStoreIdFromToken() || '토큰 없음');
      console.log('현재 사용 중인 Store Code:', getStoreCodeFromToken() || '토큰 없음');
      console.log('==========================================');

      // 로그인 확인 완료
      setIsLoggedIn(true);
    } catch (error) {
      console.error('사용자 정보 파싱 오류:', error);
    }
  }, [navigate]);
  // 기존 상태들
  const [activeTab, setActiveTab] = useState(0);
  const [, setIsSticky] = useState(false);
  const [highlightStyle, setHighlightStyle] = useState({});
  const [bannerIndex, setBannerIndex] = useState(0);
  const [imageLoadErrors, setImageLoadErrors] = useState({});
  const [, setActiveUsers] = useState(3); // eslint-disable-line no-unused-vars
  const [sliderImages, setSliderImages] = useState([]);
  const [isLoadingImages, setIsLoadingImages] = useState(true);
  const sliderImageUrls = useMemo(() => {
    return sliderImages.map(img => typeof img === 'string' ? img : img.image_url);
  }, [sliderImages]);
  const preloadedImagesRef = useRef({});
  const sliderRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState(0);
  const [hasDragged, setHasDragged] = useState(false);
  const [slideOffset, setSlideOffset] = useState(0);
  const [slideTransition, setSlideTransition] = useState(false);
  const [isLanguageExpanded, setIsLanguageExpanded] = useState(false);
  // localStorage에서 언어 설정 불러오기, 없으면 'ko' 기본값
  const [selectedLanguage, setSelectedLanguage] = useState(() => {
    return localStorage.getItem('adminLanguage') || 'ko';
  });

  // DB에서 공지사항 로드
  const loadNoticeFromDB = async () => {
    try {
      const storeId = getStoreIdFromToken() || 1;
      const response = await fetch(`/api/notices/${storeId}`);
      const data = await response.json();

      if (data.success && data.notice) {
        setNoticeText(data.notice.texts);
        setIsNoticeEnabled(data.notice.enabled);
      }
    } catch (error) {
      console.error('Failed to load notice from DB:', error);
    }
  };

  // DB에 공지사항 저장 - 필요시 사용
  // const saveNoticeToDB = async () => { ... };

  // 초기 로드 시 DB에서 공지사항 가져오기
  useEffect(() => {
    loadNoticeFromDB();
  }, []);

  // WebSocket 연결 (관리자 페이지에서도 공지사항/메뉴 동기화를 위해 필요)
  useEffect(() => {
    const storeId = getStoreIdFromToken();
    if (storeId) {
      // 관리자 페이지는 tableCode 없이 매장 전체 연결
      wsService.connect(storeId, null);
      console.log('[AdminDashboard] WebSocket 연결 완료:', storeId);

      // 메뉴 업데이트 이벤트 수신 시 메뉴 데이터 다시 불러오기
      wsService.onMenuUpdated(() => {
        console.log('[AdminDashboard] menu-updated 수신 - 메뉴 데이터 다시 불러오기');
        fetchMenusFromDB();
      });

      return () => {
        wsService.onMenuUpdated(null);
        wsService.disconnect();
        console.log('[AdminDashboard] WebSocket 연결 해제');
      };
    }
  }, []);

  const [isNoticeExpanded] = useState(true); // 관리자 페이지는 항상 확장 // eslint-disable-line no-unused-vars
  const [noticeHeight, setNoticeHeight] = useState('32px'); // eslint-disable-line no-unused-vars
  const [isNoticeEnabled, setIsNoticeEnabled] = useState(() => {
    const savedNoticeSettings = localStorage.getItem('noticeSettings');
    if (savedNoticeSettings) {
      try {
        const settings = JSON.parse(savedNoticeSettings);
        return settings.enabled !== undefined ? settings.enabled : true;
      } catch (e) {
        return true;
      }
    }
    return true;
  }); // 공지사항 활성화 상태
  const [isNoticeModalOpen, setIsNoticeModalOpen] = useState(false); // 공지사항 모달 상태
  const [tempNoticeText, setTempNoticeText] = useState(''); // 모달에서 편집 중인 임시 텍스트
  const [noticeText, setNoticeText] = useState({
    ko: '',
    en: '',
    ja: '',
    zh: ''
  }); // 다국어 공지사항 텍스트
  const [isScrollListenerActive, setIsScrollListenerActive] = useState(true);
  const [, setIsScrolling] = useState(false);
  // const [selectedMenuItem, setSelectedMenuItem] = useState(null); // 관리자 페이지 메뉴 옵션 제거
  // const [isBottomSheetOpen, setIsBottomSheetOpen] = useState(false); // 관리자 페이지 메뉴 옵션 제거
  // const [isCartOpen, setIsCartOpen] = useState(false); // 관리자 페이지 장바구니 제거
  const [, setIsOrderHistoryOpen] = useState(false); // eslint-disable-line no-unused-vars
  const [, setSkipOrderHistoryAnimation] = useState(false); // eslint-disable-line no-unused-vars
  const [, setIsStaffCallOpen] = useState(false); // eslint-disable-line no-unused-vars
  const [isMenuEditOpen, setIsMenuEditOpen] = useState(false);
  // 메뉴 추가 — 카테고리 뱃지 우측 + 버튼 클릭 시 사용 (Step 1)
  const [isAddMenuOpen, setIsAddMenuOpen] = useState(false);
  const [addMenuCategoryName, setAddMenuCategoryName] = useState('');
  const [addMenuMode, setAddMenuMode] = useState(null); // 'wowpos' | 'picker'
  const [toastMessage, setToastMessage] = useState('');
  const [isToastVisible, setIsToastVisible] = useState(false);
  
  // theme-color 메타 태그 설정
  useEffect(() => {
    let metaThemeColor = document.querySelector("meta[name=theme-color]");
    if (!metaThemeColor) {
      metaThemeColor = document.createElement('meta');
      metaThemeColor.name = "theme-color";
      document.head.appendChild(metaThemeColor);
    }
    metaThemeColor.content = "#FFFFFF";
    
    return () => {
      // 컴포넌트 언마운트 시 원래 색상으로 복원하려면
      // metaThemeColor.content = "#원래색상";
    };
  }, []);
  
  // isMenuEditOpen 상태 변경 추적
  useEffect(() => {
    // console.log('isMenuEditOpen 상태 변경됨:', isMenuEditOpen);
  }, [isMenuEditOpen]);

  // 공지 모달 열릴 때 배경 스크롤 방지 및 키보드 높이 감지
  const [keyboardHeight, setKeyboardHeight] = useState(0);
  const scrollYRef = React.useRef(0);

  useEffect(() => {
    if (isNoticeModalOpen) {
      // 현재 스크롤 위치 저장
      scrollYRef.current = window.scrollY;

      // 배경 스크롤 방지 - 모바일에서 더 확실하게 막기
      const scrollTop = window.scrollY;
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollTop}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.touchAction = 'none';
      document.documentElement.style.overflow = 'hidden';

      // 키보드 높이 감지 (visualViewport API 사용)
      const handleViewportResize = () => {
        if (window.visualViewport) {
          const viewportHeight = window.visualViewport.height;
          const fullHeight = window.innerHeight;
          const keyboardH = fullHeight - viewportHeight;
          setKeyboardHeight(keyboardH > 50 ? keyboardH : 0); // 50px 이하는 무시
        }
      };

      if (window.visualViewport) {
        window.visualViewport.addEventListener('resize', handleViewportResize);
        window.visualViewport.addEventListener('scroll', handleViewportResize);
        handleViewportResize();
      }

      return () => {
        // 스크롤 위치 복원
        document.body.style.overflow = '';
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.left = '';
        document.body.style.right = '';
        document.body.style.touchAction = '';
        document.documentElement.style.overflow = '';
        window.scrollTo(0, scrollYRef.current);
        setKeyboardHeight(0);

        if (window.visualViewport) {
          window.visualViewport.removeEventListener('resize', handleViewportResize);
          window.visualViewport.removeEventListener('scroll', handleViewportResize);
        }
      };
    }
  }, [isNoticeModalOpen]);
  const [isMenuAddOpen, setIsMenuAddOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState('');
  const [fixedScale, setFixedScale] = useState(null);
  const [isCategoryEditOpen, setIsCategoryEditOpen] = useState(false);
  const [isCallSettingsOpen, setIsCallSettingsOpen] = useState(false);
  const [isStoreSettingsOpen, setIsStoreSettingsOpen] = useState(false);
  const [isOrderOverviewOpen, setIsOrderOverviewOpen] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncMentIndex, setSyncMentIndex] = useState(0);
  const [isReviewSettingsOpen, setIsReviewSettingsOpen] = useState(false);
  // 메뉴 표시 모드 ('pos' = 모드1 POS 카테고리, 'custom' = 모드2 커스텀 카테고리)
  const [menuDisplayMode, setMenuDisplayMode] = useState('pos');
  const [isModeSwitching, setIsModeSwitching] = useState(false);
  // WowPos 영업시간 (오픈~마감, "HH:MM" | ''). WowPos 매장 전용
  const [businessOpenTime, setBusinessOpenTime] = useState('');
  const [businessCloseTime, setBusinessCloseTime] = useState('');
  const [showSaveToast, setShowSaveToast] = useState(false);
  const [saveToastError, setSaveToastError] = useState(false);
  const [showMenuToast, setShowMenuToast] = useState(false);
  const [menuToastMessage, setMenuToastMessage] = useState('');
  const [showReviewToast, setShowReviewToast] = useState(false);
  const [selectedMenuForEdit, setSelectedMenuForEdit] = useState(null);
  const [, setSelectedCategoryForEdit] = useState(''); // eslint-disable-line no-unused-vars
  // 판매시간 일괄 적용 모달 (menuEdit 저장 시 dirty 면 요청됨)
  const [bulkSalesTimeOpen, setBulkSalesTimeOpen] = useState(false);
  const [bulkSalesTimeValue, setBulkSalesTimeValue] = useState(null);
  const [bulkSoldOutOpen, setBulkSoldOutOpen] = useState(false);
  const [bulkSoldOutValue, setBulkSoldOutValue] = useState(null);
  const [storeInfo, setStoreInfo] = useState({
    storeName: '',
    businessName: '',
    address: '',
    phoneNumber: '',
    businessNumber: '',
    pos_type: undefined, // API 로드 전까지는 undefined
    origins: ''
  });
  const [isStoreInfoLoaded, setIsStoreInfoLoaded] = useState(false); // 매장 정보 로드 완료 여부
  // const [cartData, setCartData] = useState([]); // 관리자 페이지 장바구니 제거
  // const [cartCount, setCartCount] = useState(0); // 관리자 페이지 장바구니 제거
  // const [cartTotal, setCartTotal] = useState(0); // 관리자 페이지 장바구니 제거
  
  // 룰렛 애니메이션 상태 - eslint-disable-line no-unused-vars
  const [, setDisplayPrice] = useState(null); // eslint-disable-line no-unused-vars
  const [, setPrevPrice] = useState(null); // eslint-disable-line no-unused-vars
  const [, setPriceAnimating] = useState(false); // eslint-disable-line no-unused-vars
  const [, setAnimationKey] = useState(0); // eslint-disable-line no-unused-vars
  const [, setIsPriceIncreasing] = useState(true); // eslint-disable-line no-unused-vars
  const [, setIsInitialized] = useState(false); // eslint-disable-line no-unused-vars
  
  // cart-count 애니메이션 상태 - 관리자 페이지 장바구니 제거
  // const [displayCount, setDisplayCount] = useState(null);
  // const [prevCount, setPrevCount] = useState(null);
  // const [countAnimating, setCountAnimating] = useState(false);
  // const [countAnimationKey, setCountAnimationKey] = useState(0);
  // const [isCountIncreasing, setIsCountIncreasing] = useState(true);
  // const [isCountInitialized, setIsCountInitialized] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());

  // 메뉴 드래그 순서 변경 훅
  const {
    isDraggingMenu,
    isLongPressing,
    draggedMenuInfo,
    setMenuItemRef,
    setContainerRef,
    handleMenuTouchStart,
    handleMenuTouchEnd,
  } = useMenuDragReorder(async (category, newOrder) => {
    // 순서 변경 완료 콜백
    try {
      const storeId = getStoreIdFromToken();
      const categoryCode = categoryCodes[category];

      console.log('[메뉴 순서 저장] 시작:', { category, categoryCode, newOrder });

      // 1. 먼저 로컬 state를 즉시 업데이트 (UI 깜빡임 방지)
      setMenuData(prevMenuData => {
        const updatedMenuData = { ...prevMenuData };
        const currentMenus = updatedMenuData[category] || [];

        // prodCd 또는 menuId로 메뉴 찾아서 새 순서로 재배열
        const reorderedMenus = newOrder.map(orderItem => {
          return currentMenus.find(menu => {
            if (menuDisplayMode === 'custom' || storeInfo.pos_type === 'okdc') {
              // 커스텀 모드/OKDC: 코드(prod_cd 또는 menu_code)로 매칭
              return (menu.prod_cd || menu.menu_code) === orderItem.prodCd;
            } else {
              const menuIdStr = menu.id.toString().replace('menu-', '');
              return parseInt(menuIdStr) === orderItem.menuId;
            }
          });
        }).filter(Boolean);

        updatedMenuData[category] = reorderedMenus;
        return updatedMenuData;
      });

      // 2. 백그라운드에서 DB 업데이트 — 커스텀 카테고리(custom_ 코드)면 우선, 그 다음 매장별 분기
      // ※ wowpos 는 menuDisplayMode 가 'custom' 으로 강제되지만 카테고리는 wowpos_categories(WPC_) 라
      //    커스텀 분기가 아닌 wowpos 분기로 가야 함 → category_code 가 custom_ 일 때만 커스텀 처리.
      const isCustomCatCode = String(categoryCodes[category] || '').startsWith('custom_');
      if (menuDisplayMode === 'custom' && isCustomCatCode) {
        // 커스텀 모드: custom_category_items.display_order 저장 (POS 무관, menu_key = prod_cd/menu_code)
        const cid = parseInt(String(categoryCodes[category] || '').replace('custom_', ''), 10);
        const order = newOrder.map(item => item.prodCd).filter(Boolean);
        if (cid && order.length > 0) {
          await fetch(`/api/custom-categories/${cid}/items/reorder`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', ...authHeaders() },
            body: JSON.stringify({ order })
          });
        }
      } else if (storeInfo.pos_type === 'okdc') {
        const okdcOrders = newOrder.filter(item => item.prodCd).map(item => ({
          prodCd: item.prodCd,
          display_order: item.display_order
        }));
        await api.updateOKDCMenuOrder(storeId, categoryCode, okdcOrders);
      } else if (storeInfo.pos_type === 'wowpos') {
        // WowPos: 각 메뉴 display_order 개별 PUT (전용 reorder endpoint 없음)
        for (const it of newOrder) {
          if (!it.menuId) continue;
          await fetch(`/api/wowpos/menus/${it.menuId}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', ...authHeaders() },
            body: JSON.stringify({ display_order: it.display_order })
          });
        }
      } else {
        // 유니온포스 메뉴 순서 업데이트
        const menuOrders = newOrder.filter(item => item.menuId).map(item => ({
          menuId: item.menuId,
          display_order: item.display_order
        }));
        await api.updateMenuOrder(storeId, categoryCode, menuOrders);
      }

      console.log('[메뉴 순서 저장] 성공');

      // localStorage 이벤트 발생 (손님 페이지 실시간 반영)
      localStorage.setItem('menuOrderUpdated', Date.now().toString());
      window.dispatchEvent(new StorageEvent('storage', {
        key: 'menuOrderUpdated',
        newValue: Date.now().toString()
      }));

      // 성공 토스트 표시
      setMenuToastMessage('메뉴 순서가 변경되었습니다');
      setShowMenuToast(true);
      setTimeout(() => setShowMenuToast(false), 2000);

    } catch (error) {
      console.error('[메뉴 순서 저장] 실패:', error);
      // 실패 시 DB에서 다시 로드하여 원상복구
      await fetchMenusFromDB(true);
      setMenuToastMessage('메뉴 순서 저장에 실패했습니다');
      setShowMenuToast(true);
      setTimeout(() => setShowMenuToast(false), 2000);
    }
  });

  // 스크롤 컨테이너 설정 (document.documentElement 사용)
  useEffect(() => {
    setContainerRef(document.documentElement);
  }, [setContainerRef]);

  // 드래그 중 body 스크롤 및 Pull-to-Refresh 방지
  useEffect(() => {
    // body fixed 안 함 (콘텐츠 시각 잘림 회피) — overscroll-behavior 만으로
    // Pull-to-Refresh 차단. drag 중 자체 스크롤은 useMenuDragReorder 의
    // touchmove preventDefault 가 막음.
    if (isDraggingMenu) {
      document.body.style.overscrollBehavior = 'contain';
    }
    return () => {
      document.body.style.overscrollBehavior = '';
    };
  }, [isDraggingMenu]);

  // 드래그 중 뒤로가기 버튼 방지 (새로고침 방지)
  useEffect(() => {
    if (!isDraggingMenu) return;

    // 드래그 시작 시 히스토리 스택에 새 항목 추가
    window.history.pushState({ dragging: true }, '', window.location.href);

    const handlePopState = (e) => {
      // 뒤로가기 버튼 클릭 시 페이지 이동 대신 드래그만 취소
      e.preventDefault();
      // 드래그를 취소하려면 여기서 처리
      // useMenuDragReorder 훅에서 제공하는 취소 함수가 있다면 호출
      console.log('뒤로가기 방지: 드래그 중');
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [isDraggingMenu]);


  // 슬라이더 이미지가 변경될 때 bannerIndex 초기화
  useEffect(() => {
    if (sliderImages.length === 0) {
      setBannerIndex(0);
    } else if (bannerIndex >= sliderImages.length) {
      setBannerIndex(0);
    }
  }, [sliderImages.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // 시간 업데이트
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 10000); // 10초마다 업데이트
    
    return () => clearInterval(timer);
  }, []);

  // 진동 예약을 위한 ref 추가
  const pendingVibrationRef = useRef(false); // eslint-disable-line no-unused-vars
  
  // 메뉴 이용 가능 시간 확인 함수
  const calculateTimeRemaining = (item) => {
    // 판매시간이 설정되지 않았거나 비활성화된 경우
    if (!item.is_sales_time && !item.openTime) return null;

    // 새 필드명과 기존 필드명 모두 확인
    const startTime = item.start_time || item.openTime;
    const endTime = item.end_time || item.closeTime;

    if (!startTime || !endTime) return null;
    
    const now = currentTime;
    const currentHours = now.getHours();
    const currentMinutes = now.getMinutes();
    const currentTimeInMinutes = currentHours * 60 + currentMinutes;

    // 오픈/클로즈 시간을 분 단위로 변환
    const [openHour, openMinute] = startTime.split(':').map(Number);
    const [closeHour, closeMinute] = endTime.split(':').map(Number);
    let openTimeInMinutes = openHour * 60 + openMinute;
    let closeTimeInMinutes = closeHour * 60 + closeMinute;
    
    // 자정을 넘어가는 경우 처리 (예: 22:00 ~ 02:00)
    if (closeTimeInMinutes < openTimeInMinutes) {
      // 클로즈 시간이 다음날인 경우
      if (currentTimeInMinutes < closeTimeInMinutes) {
        // 현재가 자정 이후 클로즈 시간 전 (새벽 시간대)
        return null; // 주문 가능
      } else if (currentTimeInMinutes >= openTimeInMinutes) {
        // 현재가 오픈 시간 이후 (밤 시간대)
        return null; // 주문 가능
      }
    } else {
      // 일반적인 경우 (예: 10:00 ~ 22:00)
      if (currentTimeInMinutes >= openTimeInMinutes && currentTimeInMinutes < closeTimeInMinutes) {
        return null; // 주문 가능
      }
    }
    
    // 오픈까지 남은 시간 계산
    let minutesUntilOpen;
    
    if (currentTimeInMinutes < openTimeInMinutes) {
      // 같은 날 내에서 오픈 전
      minutesUntilOpen = openTimeInMinutes - currentTimeInMinutes;
    } else {
      // 현재가 클로즈 시간 이후, 다음 날 오픈까지
      minutesUntilOpen = (1440 - currentTimeInMinutes) + openTimeInMinutes;
    }
    
    const hoursUntilOpen = Math.floor(minutesUntilOpen / 60);
    const remainingMinutes = minutesUntilOpen % 60;
    
    if (hoursUntilOpen >= 1) {
      // 1시간 이상 남았을 때
      return {
        type: 'beforeOpen',
        hours: hoursUntilOpen,
        minutes: null
      };
    } else {
      // 1시간 미만 남았을 때
      return {
        type: 'beforeOpen',
        hours: null,
        minutes: remainingMinutes
      };
    }
  };
  



  // Refs
  const tabsRef = useRef(null);
  const tabRefs = useRef([]);
  const sectionRefs = useRef([]);
  const categoryHeaderRefs = useRef([]);
  const tabContainerRef = useRef(null);
  // const sliderRef = useRef(null); // 추후 사용 예정
  const autoPlayRef = useRef(null);
  const scrollTimeoutRef = useRef(null);
  // const lastScrollTop = useRef(0); // 추후 사용 예정
  const scrollEndTimeoutRef = useRef(null);
  const noticeRef = useRef(null);
  const noticeContentRef = useRef(null);
  
  // 다국어 카테고리명
  // 카테고리 번역은 이제 DB에서 관리
  const [categoryTranslations, setCategoryTranslations] = useState({});
  const [categoryCodes, setCategoryCodes] = useState({}); // 카테고리명-코드 매핑

  const [categories, setCategories] = useState([]);
  const [isLoadingMenus, setIsLoadingMenus] = useState(true);

  // DB에서 메뉴 데이터 불러오기 함수
  const fetchMenusFromDB = async (refresh = false) => {
    try {
      setIsLoadingMenus(true);
      const response = await api.getMenus(null, refresh);

      if (response.success && response.categories) {
          // 카테고리와 메뉴 데이터 처리
          const dbCategories = [];
          const dbCategoryCodes = {}; // 카테고리명과 코드 매핑
          const dbMenuData = {};
          // 커스텀 카테고리명은 메뉴 API(항상 최신)에서 오므로, 번역 localStorage 캐시보다 우선 적용
          const freshCustomCatTr = {};

          // display_order로 정렬 (백엔드에서 이미 정렬되어 있지만 확실하게 함)
          const sortedCategories = response.categories.sort((a, b) => {
            return (a.display_order || 0) - (b.display_order || 0);
          });

          sortedCategories.forEach((category, index) => {
            const categoryName = category.category_name;
            const categoryCode = category.category_code;
            console.log(`[카테고리 순서] index=${index}, name=${categoryName}, display_order=${category.display_order}`);
            dbCategories.push(categoryName);
            dbCategoryCodes[categoryName] = categoryCode; // 카테고리명-코드 매핑 저장
            dbCategoryCodes[`${categoryName}_hidden`] = category.is_hidden || false;
            // 커스텀(custom_)·와우포스(WPC_)·metacity(POS 대분류 포함) 카테고리:
            // 메뉴 API 가 항상 최신 번역을 주므로 stale 번역 localStorage 캐시를 덮어씀.
            // (okdc/union 은 카테고리 번역이 번역테이블에 있어 제외)
            if (String(categoryCode).startsWith('custom_') || String(categoryCode).startsWith('WPC_') || storeInfo?.pos_type === 'metacity' || storeInfo?.pos_type === 'toss') {
              const ko = category.name_ko || categoryName;
              freshCustomCatTr[categoryCode] = {
                ko,
                en: category.name_en || ko,
                ja: category.name_ja || ko,
                zh: category.name_zh || ko,
              };
            }

            // 메뉴 데이터 변환
            dbMenuData[categoryName] = category.menus.map(menu => ({
              id: `menu-${menu.id}`,
              db_id: menu.id, // 원본 DB ID 보존 (OKDC ASP 조회용)
              prod_cd: menu.prod_cd || menu.menu_code, // OKDC 제품 코드 (드래그 순서 변경용)
              ko: {
                name: menu.name_ko || menu.menu_name,
                description: menu.description_ko !== null && menu.description_ko !== undefined ? menu.description_ko : '',
                price: `${menu.price.toLocaleString()}원`
              },
              en: {
                name: menu.name_en || menu.menu_name,
                description: menu.description_en !== null && menu.description_en !== undefined ? menu.description_en : '',
                price: `₩${menu.price.toLocaleString()}`
              },
              ja: {
                name: menu.name_ja || menu.menu_name,
                description: menu.description_ja !== null && menu.description_ja !== undefined ? menu.description_ja : '',
                price: `₩${menu.price.toLocaleString()}`
              },
              zh: {
                name: menu.name_zh || menu.menu_name,
                description: menu.description_zh !== null && menu.description_zh !== undefined ? menu.description_zh : '',
                price: `₩${menu.price.toLocaleString()}`
              },
              soldOut: menu.is_soldout || false,
              is_soldout: menu.is_soldout || false,
              basePrice: menu.price,
              itemCode: menu.menu_code,
              image: menu.image_url || null,
              options: menu.options || [],
              menuType: menu.menu_type,
              is_popular: menu.is_popular || false,
              is_recommend: menu.is_recommend || false,
              is_new: menu.is_new || false,
              is_most_ordered: menu.is_most_ordered || false,
              is_hidden: menu.is_hidden || false,
              // 판매시간 관련 필드 추가
              is_sales_time: menu.is_sales_time || false,
              start_time: menu.start_time || null,
              end_time: menu.end_time || null,
              // 영수증 토글(METACITY): 저장 후 재조회 시 상태 유지되도록 보존
              receipt_yn: menu.receipt_yn || false,
              // 품절 자동 해제(다음 영업일 자동 재판매) — 편집 시트 재오픈 시 체크 상태 유지
              auto_resume_soldout: menu.auto_resume_soldout || false
            }));
          });

          // Load category translations from DB FIRST (before setting categories)
          try {
            const storeId = getStoreIdFromToken() || 1;

            // localStorage에서 캐시된 번역 확인
            const cacheKey = `categoryTranslations_${storeId}`;
            const cacheTimestampKey = `categoryTranslations_timestamp_${storeId}`;
            const cachedTranslations = localStorage.getItem(cacheKey);
            const cacheTimestamp = localStorage.getItem(cacheTimestampKey);
            const cacheMaxAge = 5 * 60 * 1000; // 5분 캐시

            // 캐시가 유효한지 확인
            const isCacheValid = cachedTranslations && cacheTimestamp &&
                                (Date.now() - parseInt(cacheTimestamp)) < cacheMaxAge;

            if (isCacheValid) {
              // 캐시된 데이터 사용 (단, 커스텀 카테고리명은 최신 메뉴 API 값으로 덮어씀)
              const translations = { ...JSON.parse(cachedTranslations), ...freshCustomCatTr };
              setCategoryTranslations(translations);
              console.log('Category translations loaded from cache:', translations);
            } else {
              // API에서 새로 로드
              const languages = ['ko', 'en', 'ja', 'zh'];
              const translations = {};

              // 병렬로 모든 언어 번역 로드 (더 빠름)
              const translationPromises = languages.map(async (language) => {
                try {
                  const translationResponse = await fetch(`/api/translations/categories/${storeId}/${language}`);
                  const translationData = await translationResponse.json();

                  if (translationData.success) {
                    return { language, categories: translationData.categories };
                  }
                } catch (error) {
                  console.error(`Failed to load ${language} translations:`, error);
                }
                return null;
              });

              const results = await Promise.all(translationPromises);

              results.forEach(result => {
                if (result) {
                  result.categories.forEach(cat => {
                    if (!translations[cat.category_code]) {
                      translations[cat.category_code] = {};
                    }
                    translations[cat.category_code][result.language] = cat.name;
                  });
                }
              });

              // 캐시에 저장
              localStorage.setItem(cacheKey, JSON.stringify(translations));
              localStorage.setItem(cacheTimestampKey, Date.now().toString());

              // 커스텀 카테고리명은 최신 메뉴 API 값으로 덮어씀
              setCategoryTranslations({ ...translations, ...freshCustomCatTr });
              console.log('Category translations loaded from API and cached:', translations);
            }
          } catch (error) {
            console.error('Failed to load category translations:', error);
          }

          // Set categories and menu data AFTER translations are loaded
          console.log('[fetchMenusFromDB] 카테고리 설정:', dbCategories);
          setCategories(dbCategories);
          setCategoryCodes(dbCategoryCodes); // 카테고리 코드 매핑 저장
          setMenuData(dbMenuData);

          // localStorage 캐싱 제거 - 항상 API에서 최신 데이터 가져오기
        }
    } catch (error) {
      console.error('Failed to fetch menus from DB:', error);
    } finally {
      setIsLoadingMenus(false);
    }
  };

  // 로그인 + 매장 정보 로드 완료 후 메뉴 데이터 불러오기
  // (이전 버그: storeInfo.pos_type 가 아직 undefined 일 때 fetchMenusFromDB 호출되어
  //  WowPos 매장이 기본 endpoint 로 fallback → 빈 응답. isStoreInfoLoaded 가 true 가
  //  될 때까지 기다리면 storeInfo.pos_type 가 확실히 채워진 상태에서 호출됨.)
  useEffect(() => {
    if (isLoggedIn && isStoreInfoLoaded) {
      fetchMenusFromDB(true);
    }
  }, [isLoggedIn, isStoreInfoLoaded]);
  
  const [callReasons, setCallReasons] = useState([]);
  
  const languages = [
    { code: 'zh', name: 'CN', flag: langCnIcon },
    { code: 'ja', name: 'JA', flag: langJaIcon },
    { code: 'en', name: 'EN', flag: langEnIcon },
    { code: 'ko', name: 'KO', flag: langKoIcon }
  ];
  
  // UI 텍스트 번역
  const translations = {
    ko: {
      callStaff: '직원호출',
      orderingTogether: '명이 같이 음식을 담고 있어요!',
      orders: '주문내역',
      table: ' 테이블',
      tags: {
        popular: '인기',
        recommend: '추천',
        new: '신메뉴'
      },
      businessInfo: '사업자 정보',
      originInfo: '원산지 정보',
      cart: '장바구니',
      inCart: ' in cart'
    },
    en: {
      callStaff: 'Call Staff',
      orderingTogether: ' people ordering together!',
      orders: 'Orders',
      table: 'Table ',
      tags: {
        popular: 'Popular',
        recommend: 'Recommend',
        new: 'New'
      },
      businessInfo: 'Business Info',
      originInfo: 'Country of Origin',
      cart: 'cart',
      inCart: ' in cart'
    },
    zh: {
      callStaff: '呼叫',
      orderingTogether: '人正在一起点餐！',
      orders: '订单记录',
      table: '号桌',
      tags: {
        popular: '人气',
        recommend: '推荐',
        new: '新品'
      },
      businessInfo: '商户信息',
      originInfo: '原产地信息',
      cart: '查看购物车',
      inCart: ' in cart'
    },
    ja: {
      callStaff: '呼出',
      orderingTogether: '名で一緒に注文中です！',
      orders: '注文履歴',
      table: 'テーブル',
      tags: {
        popular: '人気',
        recommend: 'おすすめ',
        new: '新メニュー'
      },
      businessInfo: '事業者情報',
      originInfo: '原産地情報',
      cart: 'カート',
      inCart: ' in cart'
    }
  };
  
  const t = translations[selectedLanguage] || translations.ko;
  

  // banbanCombo는 추후 사용 예정
  // const banbanCombo = {
  //   id: 'banban-combo-1',
  //   ko: { name: '반반콤보', description: '달콤한 허니소스와 바삭한 치킨의 완벽한 조화', price: '29,000원' },
  //   en: { name: 'Half & Half Combo', description: 'Perfect harmony of sweet honey sauce and crispy chicken', price: '₩29,000' },
  //   ja: { name: 'ハーフ&ハーフコンボ', description: '甘いハニーソースとサクサクチキンの完璧な調和', price: '₩29,000' },
  //   zh: { name: '半半套餐', description: '甜蜜蜂蜜酱与酥脆炸鸡的完美融合', price: '₩29,000' },
  //   tags: ['popular', 'new'],
  //   options: {
  //     '음료 추가 선택': {
  //       type: 'required', //필수수
  //       items: [
  //         { name: '콜라 1.25L', price: 0 },
  //         { name: '사이다 1.25L', price: 0 },
  //         { name: '환타 1.25L', price: 0 }
  //       ]
  //     },
  //     '맵기 선택': {
  //       type: 'required',
  //       items: [
  //         { name: '순한맛', price: 0 },
  //         { name: '보통맛', price: 0 },
  //         { name: '매운맛', price: 0 }
  //       ]
  //     },
  //     '사이드메뉴 추가': {
  //       type: 'optional', //선택택
  //       items: [
  //         { name: '치킨무', price: 1000 },
  //         { name: '떡볶이', price: 3000 },
  //         { name: '감자튀김', price: 2500 }
  //       ]
  //     }
  //   }
  // };


  // 다국어 메뉴 데이터
  const [menuData, setMenuData] = useState({});

  // 메뉴 데이터가 변경될 때마다 이벤트 발생
  useEffect(() => {
    if (Object.keys(menuData).length === 0 || isLoadingMenus) return;

    // 같은 탭의 다른 컴포넌트에 알림
    window.dispatchEvent(new CustomEvent('menuDataUpdated', {
      detail: { menuData }
    }));
  }, [menuData, isLoadingMenus]);

  // 카테고리 번역 데이터 별도 저장
  useEffect(() => {
    // localStorage 사용 중단 - DB에서 관리
    // 카테고리 번역은 이제 DB의 translations API를 통해 처리
  }, [categoryTranslations]);

  // 공지사항 설정이 변경될 때마다 localStorage에 저장
  useEffect(() => {
    const noticeSettings = {
      enabled: isNoticeEnabled,
      texts: noticeText
    };
    localStorage.setItem('noticeSettings', JSON.stringify(noticeSettings));
  }, [isNoticeEnabled, noticeText]);

  // 호출 설정이 변경될 때마다 localStorage에 저장
  // DB에서 호출 사유 로드 (언어별)
  useEffect(() => {
    const loadCallReasons = async () => {
      try {
        const response = await api.getCallReasons(selectedLanguage);
        if (response.success && response.reasons) {
          // id와 텍스트를 함께 저장
          const reasonsData = response.reasons.map(r => ({
            id: r.id,
            text: r.reason_text,
            originalText: r.original_text
          }));
          setCallReasons(reasonsData);
        }
      } catch (e) {
        console.error('Failed to load call reasons from DB:', e);
      }
    };
    loadCallReasons();
  }, [selectedLanguage]); // 언어 변경 시 다시 로드

  // 호출 사유 순서 변경은 명시적으로 요청할 때만 수행
  // (자동 저장 제거 - 순서 변경 시에만 저장)

  // 슬라이더 이미지가 변경될 때마다 localStorage에 저장
  useEffect(() => {
    try {
      localStorage.setItem('sliderImages', JSON.stringify(sliderImages));
    } catch (e) {
      console.error('Failed to save slider images:', e);
    }
  }, [sliderImages]);

  // localStorage 정리 함수
  const cleanupLocalStorage = () => {
    // console.log('Cleaning up localStorage...');
    
    // 불필요한 키 제거
    const keysToRemove = ['multilingualMenuData', 'orderHistory', 'oldMenuData'];
    keysToRemove.forEach(key => {
      if (localStorage.getItem(key)) {
        localStorage.removeItem(key);
        // console.log(`Removed: ${key}`);
      }
    });
  };

  // 매장 정보 로드 및 localStorage 정리
  useEffect(() => {
    // localStorage 정리 (한 번만 실행)
    const cleanupExecuted = sessionStorage.getItem('localStorageCleanupDone');
    if (!cleanupExecuted) {
      cleanupLocalStorage();
      sessionStorage.setItem('localStorageCleanupDone', 'true');
    }

    const loadStoreInfo = async () => {
      try {
        console.log('[매장 정보 로드] 시작');
        const storeId = getStoreIdFromToken();
        console.log('[매장 정보 로드] storeId:', storeId);
        if (!storeId) {
          console.error('[매장 정보 로드] Store ID not found');
          return;
        }

        // 매장 정보 로드
        const storeResponse = await api.getStoreInfo(storeId);

        // API 응답 구조: response.store (response.storeInfo가 아님)
        const storeData = storeResponse.storeInfo || storeResponse.store;

        if (storeResponse.success && storeData) {
          const newStoreInfo = {
            id: storeId,
            store_id: storeId,
            storeName: storeData.store_name || '',
            businessName: storeData.business_name || '',
            address: storeData.business_address || '',
            phoneNumber: storeData.business_phone || '',
            businessNumber: storeData.business_number || '',
            origins: storeData.ingredient_info || '',
            pos_type: storeData.pos_type || ''
          };
          setStoreInfo(newStoreInfo);
          setIsStoreInfoLoaded(true); // 매장 정보 로드 완료

          // 메뉴 표시 모드 로드 — WowPos 매장은 POS 동기화 없으니 무조건 custom 강제
          try {
            const modeRes = await fetch(`/api/stores/${storeId}/menu-display-mode`);
            const modeData = await modeRes.json();
            const dbMode = modeData.success ? (modeData.mode || 'pos') : 'pos';
            const effectiveMode = storeData.pos_type === 'wowpos' ? 'custom' : dbMode;
            setMenuDisplayMode(effectiveMode);
          } catch (e) {
            console.error('[메뉴 표시 모드 로드] 실패:', e);
            if (storeData.pos_type === 'wowpos') setMenuDisplayMode('custom');
          }


          // 영업시간 로드 — 전 POS 공통
          try {
            const bhRes = await fetch(`/api/wowpos/stores/${storeId}/business-hours`, {
              headers: authHeaders()
            });
            const bhData = await bhRes.json();
            if (bhData.success) {
              setBusinessOpenTime(bhData.openTime || '');
              setBusinessCloseTime(bhData.closeTime || '');
            }
          } catch (e) {
            console.error('[영업시간 로드] 실패:', e);
          }
        }
      } catch (error) {
        console.error('[매장 정보 로드] 에러:', error);
        setIsStoreInfoLoaded(true); // 에러가 발생해도 로드 완료 처리
      }
    };

    // 초기 로드
    loadStoreInfo();

    // S3에서 배너 이미지 로드
    fetchBannerImages();
  }, []);

  // S3에서 배너 이미지 가져오기
  const fetchBannerImages = async () => {
    try {
      console.log('배너 이미지 가져오기 시작');
      setIsLoadingImages(true); // 로딩 시작
      const storeId = getStoreIdFromToken() || 1;
      console.log('배너 이미지 조회 storeId:', storeId);
      const response = await fetch(`/api/banners/${storeId}`);
      if (response.ok) {
        const data = await response.json();
        console.log('받은 배너 데이터:', data);
        if (data.success && data.images.length > 0) {
          // ID와 URL 정보를 모두 저장
          console.log('설정할 이미지들:', data.images);
          setSliderImages(data.images);

          // 사용자 페이지에 업데이트 알림
          localStorage.setItem('bannersUpdated', Date.now().toString());
        } else {
          // 이미지가 없으면 빈 배열 설정
          console.log('이미지 없음 - 빈 배열 설정');
          setSliderImages([]);

          // 사용자 페이지에 업데이트 알림
          localStorage.setItem('bannersUpdated', Date.now().toString());
        }
      }
      setIsLoadingImages(false); // 로딩 완료
    } catch (error) {
      console.error('Failed to fetch banner images:', error);
      setSliderImages([]);
      setIsLoadingImages(false); // 오류 시에도 로딩 완료
    }
  };

  // 메뉴 확장

  // 리플 효과를 위한 ref
  const currentRippleRef = useRef(null);

  // 리플 효과 생성
  const createRipple = (element) => {
    // console.log('createRipple 호출됨');
    // 기존 리플이 있으면 제거
    if (currentRippleRef.current) {
      currentRippleRef.current.remove();
      currentRippleRef.current = null;
    }
    
    const ripple = document.createElement('span');
    ripple.classList.add('ripple');
    
    // 요소의 크기 가져오기
    const rect = element.getBoundingClientRect();
    const size = Math.max(rect.width, rect.height) * 2;
    
    // 정중앙에 배치
    ripple.style.width = ripple.style.height = size + 'px';
    ripple.style.left = '50%';
    ripple.style.top = '50%';
    ripple.style.marginLeft = `-${size / 2}px`;
    ripple.style.marginTop = `-${size / 2}px`;
    
    // 리플 추가
    element.appendChild(ripple);
    
    // 애니메이션 시작
    ripple.classList.add('expand');
    
    // 애니메이션 종료 후 제거
    ripple.addEventListener('animationend', () => {
      ripple.remove();
    }, { once: true });
    
    return ripple;
  };

  // 하이라이트 위치 업데이트
  const updateHighlight = (tabIndex) => {
    if (tabRefs.current[tabIndex]) {
      const tab = tabRefs.current[tabIndex];
      const isCurrentlyActive = tab.classList.contains('active');
      
      if (isCurrentlyActive) {
        setHighlightStyle({
          left: tab.offsetLeft,
          width: tab.offsetWidth,
        });
        return;
      }
      
      const tabText = tab.innerText;
      const tempDiv = document.createElement('div');
      tempDiv.style.cssText = `
        position: absolute;
        visibility: hidden;
        font-size: 15px;
        font-weight: 700;
        white-space: nowrap;
        font-family: "Pretendard", -apple-system, BlinkMacSystemFont, system-ui, Roboto, "Helvetica Neue", "Segoe UI", "Apple SD Gothic Neo", "Noto Sans KR", "Malgun Gothic", sans-serif;
        line-height: 18px;
        letter-spacing: 0;
      `;
      tempDiv.textContent = tabText;
      document.body.appendChild(tempDiv);
      
      const predictedWidth = tempDiv.offsetWidth;
      document.body.removeChild(tempDiv);
      
      const currentCenter = tab.offsetLeft + tab.offsetWidth / 2;
      const newLeft = currentCenter - predictedWidth / 2;
      
      setHighlightStyle({
        left: newLeft,
        width: predictedWidth,
      });
    }
  };

  // 탭을 화면 중앙으로 스크롤하는 함수
  const scrollTabToCenter = (tabIndex) => {
    const container = tabContainerRef.current;
    const tab = tabRefs.current[tabIndex];
    
    if (container && tab) {
      const containerWidth = container.offsetWidth;
      const tabLeft = tab.offsetLeft;
      const tabWidth = tab.offsetWidth;
      
      const tabCenter = tabLeft + (tabWidth / 2);
      const containerCenter = containerWidth / 2;
      const scrollLeft = tabCenter - containerCenter;
      
      container.scrollTo({
        left: scrollLeft,
        behavior: 'smooth'
      });
    }
  };

  // 탭 클릭 핸들러
  const handleTabClick = (index) => {
    console.log('🔵 카테고리 클릭:', index);

    // 이전 스크롤 타이머들 취소
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }
    if (scrollEndTimeoutRef.current) {
      clearTimeout(scrollEndTimeoutRef.current);
    }

    // 즉시 스크롤 리스너 비활성화
    setIsScrollListenerActive(false);
    setActiveTab(index);
    scrollTabToCenter(index);

    if (categoryHeaderRefs.current[index]) {
      const tabsHeight = tabsRef.current?.offsetHeight || 51;
      const categoryBadgeContainer = categoryHeaderRefs.current[index];

      const rect = categoryBadgeContainer.getBoundingClientRect();
      const scrollTop = document.body.scrollTop || window.pageYOffset;
      const elementTop = rect.top + scrollTop;

      const targetPosition = elementTop - tabsHeight - 40;

      const scrollDistance = Math.abs(targetPosition - scrollTop);

      console.log('📊 스크롤 정보:', {
        현재스크롤: scrollTop,
        'body.scrollTop': document.body.scrollTop,
        'window.pageYOffset': window.pageYOffset,
        요소위치: elementTop,
        목표위치: targetPosition,
        이동거리: scrollDistance,
        탭높이: tabsHeight
      });

      // 스크롤 시간 계산 (더 정확하게)
      const scrollDuration = Math.min(Math.max(scrollDistance * 0.5, 400), 800);

      // body가 스크롤 컨테이너이므로 body.scrollTo 사용
      document.body.scrollTo({
        top: targetPosition,
        behavior: 'smooth'
      });

      console.log('✅ body.scrollTo 호출 완료');

      // 스크롤 애니메이션이 완전히 끝난 후에만 리스너 활성화
      scrollTimeoutRef.current = setTimeout(() => {
        setIsScrollListenerActive(true);
      }, scrollDuration + 500);
    } else {
      console.log('⚠️ categoryHeaderRefs가 없음:', index);
      // 스크롤할 필요가 없는 경우에도 약간의 딜레이 후 활성화
      setTimeout(() => {
        setIsScrollListenerActive(true);
      }, 300);
    }
  };

  // 비디오 URL 감지 (mp4/webm/mov/m4v/ogv)
  const isVideoUrl = (url) => typeof url === 'string' && /\.(mp4|webm|mov|m4v|ogv)(\?|$)/i.test(url);

  // 슬라이더 이미지 프리로드 (모든 이미지를 Image 객체로 캐싱 + 에러 감지)
  // 비디오는 Image 객체로 디코드 불가하므로 건너뜀 — <video> 엘리먼트가 자체 로딩 처리
  useEffect(() => {
    if (sliderImages.length === 0) return;
    const imageUrls = sliderImages.map(img => typeof img === 'string' ? img : img.image_url);
    imageUrls.forEach((url, index) => {
      // 비디오 URL은 프리로드 스킵 (Image 객체로 디코드 실패 → 잘못된 isError 발생 방지)
      if (isVideoUrl(url)) {
        setImageLoadErrors(prev => ({ ...prev, [index]: false }));
        return;
      }
      if (!preloadedImagesRef.current[url]) {
        const img = new Image();
        img.onload = () => {
          setImageLoadErrors(prev => ({ ...prev, [index]: false }));
        };
        img.onerror = () => {
          setImageLoadErrors(prev => ({ ...prev, [index]: true }));
        };
        img.src = url;
        preloadedImagesRef.current[url] = img;
      }
    });
  }, [sliderImages]);

  const getSliderWidth = () => {
    return sliderRef.current ? sliderRef.current.offsetWidth : 360;
  };

  // 슬라이드별 video element refs
  const bannerVideoRefs = useRef({});

  // bannerIndex 최신값 참조 (setInterval closure 우회)
  const bannerIndexRef = useRef(bannerIndex);
  useEffect(() => {
    bannerIndexRef.current = bannerIndex;
  }, [bannerIndex]);

  // bannerIndex 변경 시: 활성 비디오는 바로 재생(첫 프레임 이미 렌더됨), 비활성은 멈추고 0초로 되감기
  // 비활성을 0초로 되감아야 다음에 활성화될 때 첫 프레임이 보이는 상태에서 시작 → 검정 깜빡임 제거
  useEffect(() => {
    Object.entries(bannerVideoRefs.current).forEach(([key, video]) => {
      if (!video) return;
      const idx = parseInt(key, 10);
      if (idx === bannerIndex) {
        video.play().catch(() => {});
      } else {
        try { video.pause(); video.currentTime = 0; } catch (e) {}
      }
    });
  }, [bannerIndex]);

  const startAutoPlay = () => {
    if (sliderImages.length <= 1) return;
    stopAutoPlay();
    autoPlayRef.current = setInterval(() => {
      // 현재 슬라이드가 비디오면 자동 advance 안 함 — onEnded로 처리
      const cur = bannerIndexRef.current;
      if (isVideoUrl(sliderImageUrls[cur])) return;
      goToNext();
    }, 2500);
  };

  const stopAutoPlay = () => {
    if (autoPlayRef.current) {
      clearInterval(autoPlayRef.current);
    }
  };

  const goToSlide = (index) => {
    setBannerIndex(index);
    setSlideOffset(0);
    setSlideTransition(false);
  };

  const isAnimatingRef = useRef(false);

  const goToNext = () => {
    if (sliderImages.length <= 1 || isAnimatingRef.current) return;
    isAnimatingRef.current = true;
    setSlideTransition(true);
    setSlideOffset(-getSliderWidth());
  };

  const goToPrev = () => {
    if (sliderImages.length <= 1 || isAnimatingRef.current) return;
    isAnimatingRef.current = true;
    setSlideTransition(true);
    setSlideOffset(getSliderWidth());
  };

  const handleSlideTransitionEnd = (e) => {
    if (e.target !== e.currentTarget) return;
    const w = getSliderWidth();
    if (slideOffset <= -w) {
      setBannerIndex(prev => (prev + 1) % sliderImages.length);
    } else if (slideOffset >= w) {
      setBannerIndex(prev => (prev - 1 + sliderImages.length) % sliderImages.length);
    }
    setSlideTransition(false);
    setSlideOffset(0);
    isAnimatingRef.current = false;
  };

  const handleDragStart = (clientX) => {
    if (isAnimatingRef.current) return;
    setIsDragging(true);
    setDragStart(clientX);
    setSlideOffset(0);
    setSlideTransition(false);
    setHasDragged(false);
    stopAutoPlay();
  };

  const handleDragMove = (clientX) => {
    if (!isDragging) return;
    const offset = clientX - dragStart;
    setSlideOffset(offset);
    if (Math.abs(offset) > 10) {
      setHasDragged(true);
    }
  };

  const handleDragEnd = () => {
    if (!isDragging) return;
    setIsDragging(false);

    const threshold = 50;
    setSlideTransition(true);
    if (slideOffset > threshold) {
      isAnimatingRef.current = true;
      setSlideOffset(getSliderWidth());
    } else if (slideOffset < -threshold) {
      isAnimatingRef.current = true;
      setSlideOffset(-getSliderWidth());
    } else {
      setSlideOffset(0);
    }

    if (sliderImages.length > 1) {
      startAutoPlay();
    }
  };

  const handleMouseDown = (e) => {
    e.preventDefault();
    handleDragStart(e.clientX);
  };

  const handleMouseMove = useCallback((e) => {
    handleDragMove(e.clientX);
  }, [handleDragMove]);

  const handleMouseUp = useCallback(() => {
    handleDragEnd();
  }, [handleDragEnd]);

  const handleTouchStart = (e) => {
    handleDragStart(e.touches[0].clientX);
  };

  const handleTouchMove = (e) => {
    handleDragMove(e.touches[0].clientX);
  };

  const handleTouchEnd = () => {
    handleDragEnd();
  };

  // 장바구니 데이터 로드 및 계산
  // 관리자 페이지 장바구니 기능 제거
  /*
  const loadCartData = () => {
    const storedCart = JSON.parse(localStorage.getItem('cartData') || '[]');
    setCartData(storedCart);
    
    // 총 수량 계산
    const totalCount = storedCart.reduce((sum, item) => sum + item.quantity, 0);
    setCartCount(totalCount);
    
    // 총 가격 계산
    const totalPrice = storedCart.reduce((sum, item) => {
      const basePrice = parseInt(item.menuItem.price.replace(/[^0-9]/g, '')) || 0;
      let optionsPrice = 0;
      
      if (item.selectedOptions && item.menuItem.options) {
        Object.entries(item.menuItem.options).forEach(([optionTitle, optionData], index) => {
          const selected = item.selectedOptions[index];
          
          if (optionData.type === 'required' && selected) {
            const option = optionData.items.find(opt => opt.name === selected);
            if (option && option.price) {
              optionsPrice += option.price;
            }
          } else if (optionData.type === 'optional' && Array.isArray(selected)) {
            selected.forEach(optionName => {
              const option = optionData.items.find(opt => opt.name === optionName);
              if (option && option.price) {
                optionsPrice += option.price;
              }
            });
          }
        });
      }
      
      return sum + ((basePrice + optionsPrice) * item.quantity);
    }, 0);
    
    setCartTotal(totalPrice);
  };
  */

  // 컴포넌트 마운트시 장바구니 데이터 로드 - 관리자 페이지 제거
  /*
  useEffect(() => {
    loadCartData();
    
    // localStorage 변경 감지 (다른 탭에서 변경시)
    const handleStorageChange = () => {
      loadCartData();
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    // BroadcastChannel로 실시간 동기화
    let channel;
    try {
      channel = new BroadcastChannel('cart_channel');
      channel.onmessage = (event) => {
        if (event.data.type === 'cart_updated') {
          loadCartData();
        } else if (event.data.type === 'cart-cleared') {
          // 장바구니가 비워졌을 때
          setCartData([]);
          setCartCount(0);
          setCartTotal(0);
        }
      };
    } catch (error) {
      // console.log('BroadcastChannel not supported:', error);
    }
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      if (channel) {
        channel.close();
      }
    };
  }, []);
  */

  // 옵션 화면이 닫힐 때마다 장바구니 데이터 새로고침 - 관리자 페이지 제거
  /*
  useEffect(() => {
    if (!isBottomSheetOpen) {
      loadCartData();
    }
  }, [isBottomSheetOpen]);
  */

  useEffect(() => {
    if (noticeRef.current) {
      // 관리자 페이지는 항상 확장 상태
      // 확장 상태에서는 스크롤 높이 계산
      // 텍스트 높이만 측정하고 패딩은 CSS에서 처리
      const textElement = noticeRef.current.querySelector('.notice-text');
      if (textElement) {
        const textHeight = textElement.scrollHeight;
        const padding = 16; // top 8px + bottom 8px
        const totalHeight = textHeight + padding;
        setNoticeHeight(`${totalHeight}px`);
      }
    }
  }, [isNoticeExpanded, noticeText, selectedLanguage]);
  
  // 가격 변경 시 슬롯머신 효과 - 관리자 페이지 장바구니 제거
  /*
  useEffect(() => {
    const newPrice = cartTotal.toLocaleString();
    
    // 장바구니가 비어있을 때
    if (cartCount === 0) {
      setDisplayPrice('0');
      setPrevPrice('0');
      setIsInitialized(false);
      return;
    }
    
    // 초기화되지 않은 상태에서 처음 장바구니에 아이템이 추가될 때
    if (!isInitialized && cartCount > 0) {
      // 애니메이션 없이 바로 현재 가격으로 설정
      setDisplayPrice(newPrice);
      setPrevPrice(newPrice);
      setIsInitialized(true);
      return;
    }
    
    // 일반적인 가격 변경 (초기화 이후)
    if (isInitialized && newPrice !== displayPrice && !priceAnimating) {
      setPrevPrice(displayPrice);
      setAnimationKey(prev => prev + 1);
      
      // 가격 증가/감소 판단
      const prevPriceNum = parseInt(displayPrice.replace(/,/g, '')) || 0;
      const newPriceNum = parseInt(newPrice.replace(/,/g, '')) || 0;
      setIsPriceIncreasing(newPriceNum > prevPriceNum);
      
      setPriceAnimating(true);
      
      // 실제 가격 업데이트는 50ms 후
      setTimeout(() => {
        setDisplayPrice(newPrice);
      }, 50);
      
      // 애니메이션 종료
      setTimeout(() => {
        setPriceAnimating(false);
      }, 600);
    }
  }, [cartTotal, cartCount, displayPrice, priceAnimating, isInitialized]);
  */
  
  // cart-count 변경 시 애니메이션 - 관리자 페이지 장바구니 제거
  /*
  useEffect(() => {
    // 장바구니가 비어있을 때
    if (cartCount === 0) {
      setDisplayCount(0);
      setPrevCount(0);
      setIsCountInitialized(false);
      return;
    }
    
    // 초기화되지 않은 상태에서 처음 장바구니에 아이템이 추가될 때
    if (!isCountInitialized && cartCount > 0) {
      // 애니메이션 없이 바로 현재 수량으로 설정
      setDisplayCount(cartCount);
      setPrevCount(cartCount);
      setIsCountInitialized(true);
      return;
    }
    
    // 일반적인 수량 변경 (초기화 이후)
    if (isCountInitialized && cartCount !== displayCount && !countAnimating) {
      setPrevCount(displayCount);
      setCountAnimationKey(prev => prev + 1);
      setIsCountIncreasing(cartCount > displayCount);
      setCountAnimating(true);
      
      // 실제 수량 업데이트는 약간의 딜레이 후
      setTimeout(() => {
        setDisplayCount(cartCount);
      }, 50);
      
      // 애니메이션 종료
      setTimeout(() => {
        setCountAnimating(false);
      }, 300);
    }
  }, [cartCount, displayCount, countAnimating, isCountInitialized]);
  */
  
  // 스크롤 감지 useEffect
  useEffect(() => {
    let scrollTimeout;

    const handleScroll = () => {
      setIsScrolling(true);

      // 기존 타임아웃 취소
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }

      // 스크롤이 멈춘 후 100ms 후에 스크롤 상태 해제 (빠른 반응)
      scrollTimeout = setTimeout(() => {
        setIsScrolling(false);
      }, 100);
    };

    // body가 스크롤 컨테이너이므로 body에 이벤트 리스너 등록
    document.body.addEventListener('scroll', handleScroll, { passive: true });
    document.body.addEventListener('touchmove', handleScroll, { passive: true });

    return () => {
      document.body.removeEventListener('scroll', handleScroll);
      document.body.removeEventListener('touchmove', handleScroll);
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (tabsRef.current) {
        const tabsTop = tabsRef.current.offsetTop;
        const scrollTop = document.body.scrollTop || window.pageYOffset;
        setIsSticky(scrollTop > tabsTop);
      }
    };

    // body가 스크롤 컨테이너이므로 body에 이벤트 리스너 등록
    document.body.addEventListener('scroll', handleScroll);
    return () => document.body.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleUserScroll = () => {
      if (!isScrollListenerActive) {
        return;
      }

      const tabsHeight = tabsRef.current?.offsetHeight || 51;
      const triggerPoint = tabsHeight + 120;

      let newActiveTab = 0;

      for (let i = categoryHeaderRefs.current.length - 1; i >= 0; i--) {
        const categoryBadgeContainer = categoryHeaderRefs.current[i];
        if (categoryBadgeContainer) {
          const rect = categoryBadgeContainer.getBoundingClientRect();
          const badgeTop = rect.top;

          if (badgeTop <= triggerPoint) {
            newActiveTab = i;
            break;
          }
        }
      }

      if (newActiveTab !== activeTab) {
        setActiveTab(newActiveTab);
        scrollTabToCenter(newActiveTab);
      }

    };

    // body가 스크롤 컨테이너이므로 body에 이벤트 리스너 등록
    document.body.addEventListener('scroll', handleUserScroll, { passive: true });

    setTimeout(handleUserScroll, 100);

    return () => {
      document.body.removeEventListener('scroll', handleUserScroll);
    };
  }, [activeTab, isScrollListenerActive, categories]);

  useEffect(() => {
    updateHighlight(activeTab);
  }, [activeTab]);
  
  // 카테고리 또는 언어가 변경될 때 하이라이트 재계산
  useEffect(() => {
    // 약간의 지연을 주어 DOM이 업데이트된 후 실행
    const timer = setTimeout(() => {
      updateHighlight(activeTab);
    }, 50);
    
    return () => clearTimeout(timer);
  }, [categories, selectedLanguage, activeTab]);

  useEffect(() => {
    const timer = setTimeout(() => {
      scrollTabToCenter(0);
    }, 50);
    
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (sliderImages.length > 1) {
      startAutoPlay();
    }
    return () => stopAutoPlay();
  }, [sliderImages.length]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isDragging) {
      const handleGlobalMouseMove = (e) => handleMouseMove(e);
      const handleGlobalMouseUp = () => {
        handleMouseUp();
      };
      
      document.addEventListener('mousemove', handleGlobalMouseMove);
      document.addEventListener('mouseup', handleGlobalMouseUp);
      
      return () => {
        document.removeEventListener('mousemove', handleGlobalMouseMove);
        document.removeEventListener('mouseup', handleGlobalMouseUp);
      };
    }
  }, [isDragging, dragStart, handleMouseMove, handleMouseUp]);


  useEffect(() => {
    const scaleUI = () => {
      // 스케일이 고정되어 있으면 재계산하지 않음
      if (fixedScale !== null) {
        return;
      }
      
      const wrapper = document.querySelector('.scale-wrapper');
      if (wrapper) {
        const viewportWidth = window.innerWidth;
        const scale = Math.min(viewportWidth / 360, 1.5);
        wrapper.style.zoom = scale;
      }
    };

    scaleUI();
    window.addEventListener('resize', scaleUI);
    return () => window.removeEventListener('resize', scaleUI);
  }, [fixedScale]);


  useEffect(() => {
    const scrollTimeout = scrollTimeoutRef.current;
    const scrollEndTimeout = scrollEndTimeoutRef.current;
    const autoPlay = autoPlayRef.current;
    return () => {
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }
      if (scrollEndTimeout) {
        clearTimeout(scrollEndTimeout);
      }
      if (autoPlay) {
        clearInterval(autoPlay);
      }
      // 컴포넌트 언마운트 시 스크롤 해제
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
      document.body.style.paddingRight = '';
    };
  }, []);

  // 바텀시트 상태 변경 시 스크롤 처리 및 스케일 고정
  useEffect(() => {
    if (isMenuEditOpen) {
      // 바텀시트가 열릴 때 현재 스케일 고정
      const wrapper = document.querySelector('.scale-wrapper');
      if (wrapper) {
        const currentScale = wrapper.style.zoom || 1;
        setFixedScale(currentScale);
      }
    } else {
      // 바텀시트가 닫혔을 때
      // 스크롤 확실히 해제
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
      document.body.style.paddingRight = '';
      
      const scaleWrapper = document.querySelector('.scale-wrapper');
      if (scaleWrapper) {
        scaleWrapper.style.paddingRight = '';
      }
      
      // 스케일 고정 해제
      setFixedScale(null);
    }
  }, [isMenuEditOpen]);

  // 메뉴 클릭 핸들러
  // 관리자 페이지 메뉴 클릭 기능 제거
  /*
  const handleMenuClick = (item) => {
    setSelectedMenuItem(item);
    setIsBottomSheetOpen(true);
  };

  const handleBottomSheetClose = () => {
    setIsBottomSheetOpen(false);
    setTimeout(() => {
      setSelectedMenuItem(null);
    }, 300);
  };
  */

  // 토스트 팝업 표시 함수
  const showToast = (message) => {
    setIsToastVisible(false);

    setTimeout(() => {
      setToastMessage(message);
      setIsToastVisible(true);

      setTimeout(() => {
        setTimeout(() => {
          setIsToastVisible(false);
        }, 300);
      }, 2000); // 2초 후 페이드아웃
    }, 10);
  };

  const syncMents = [
    '메뉴 데이터를 가져오고 있어요',
    '거의 다 됐어요!',
    '조금만 기다려주세요...',
    '맛있는 메뉴를 정리하고 있어요',
    '데이터를 꼼꼼히 확인하고 있어요',
    '포스에서 메뉴를 불러오는 중...',
  ];

  const syncMentIntervalRef = useRef(null);

  const handleSyncStart = async (posType) => {
    setIsSyncing(true);
    document.body.style.overflow = 'hidden';
    setSyncMentIndex(0);

    syncMentIntervalRef.current = setInterval(() => {
      setSyncMentIndex(prev => (prev + 1) % syncMents.length);
    }, 4500);

    try {
      if (posType === 'toss') {
        // 토스 카탈로그는 POS 안의 워커만 읽을 수 있다. 서버가 워커에게 요청하고,
        // 워커가 보고해 적재되기를 기다린 뒤 응답이 온다.
        const storeId = getStoreIdFromToken();
        const result = await apiRequest('/toss/sync', {
          method: 'POST',
          body: JSON.stringify({ storeId }),
        });
        if (!result.success) throw new Error(result.error || '동기화 실패');
        await apiRequest(`/store/${storeId}/menus?refresh=true`);
      } else if (posType === 'okdc' || posType === 'metacity') {
        const result = posType === 'metacity' ? await syncMetacityMasterData() : await syncOKDCMasterData();
        if (result.success) {
          const storeId = getStoreIdFromToken();
          for (let i = 0; i < 15; i++) {
            await new Promise(r => setTimeout(r, 2000));
            try {
              const menuRes = await apiRequest(`/store/${storeId}/menus?refresh=true`);
              if (menuRes.success && menuRes.categories && menuRes.categories.length > 0) {
                break;
              }
            } catch (e) {}
          }
        }
      } else {
        await syncAllUnionData();
      }
      fetchMenusFromDB();
      showToast(`<svg width="16" height="12" viewBox="0 0 16 12" fill="none" xmlns="http://www.w3.org/2000/svg" style="display: inline-block; vertical-align: middle; margin-right: 6px;"><path d="M5.75416 8.8875L13.5229 1.11875C13.7062 0.935417 13.9201 0.84375 14.1646 0.84375C14.409 0.84375 14.6229 0.935417 14.8062 1.11875C14.9896 1.30208 15.0812 1.51994 15.0812 1.77233C15.0812 2.02472 14.9896 2.24228 14.8062 2.425L6.39583 10.8583C6.21249 11.0417 5.9986 11.1333 5.75416 11.1333C5.50971 11.1333 5.29583 11.0417 5.11249 10.8583L1.17083 6.91667C0.987492 6.73333 0.899492 6.51578 0.906826 6.264C0.914159 6.01222 1.0098 5.79436 1.19374 5.61042C1.37769 5.42647 1.59555 5.33481 1.84733 5.33542C2.10033 5.33603 2.32694 5.42958 2.51458 5.61625L5.75416 8.8875Z" fill="#FFD700"/></svg>포스와 동기화가 완료되었습니다`);
    } catch (error) {
      showToast(`<img src="${refuseIcon}" style="width: 12px; height: 12px; vertical-align: middle; margin-right: 8px;" alt="x"/><span style="color: white; font-weight: 600; font-size: 14px;">동기화에 실패했습니다</span>`);
    } finally {
      clearInterval(syncMentIntervalRef.current);
      document.body.style.overflow = '';
      setIsSyncing(false);
    }
  };

  // 매장 설정 모달용 콜백 메모화 — 비동기 fetch에 의한 re-render 시에도 identity 안정 유지
  // (이전 버그: re-render마다 새 화살표 함수 → 자식 popstate listener cleanup/재등록 churn)
  const handleStoreSettingsClose = useCallback(() => {
    setIsStoreSettingsOpen(false);
  }, []);

  const handleOrderOverviewClose = useCallback(() => {
    setIsOrderOverviewOpen(false);
  }, []);

  // 나머지 관리자 모달들의 onClose도 동일 이유로 메모화 (popstate listener 안정화)
  // ⚠️ deps 에 storeInfo?.pos_type 추가 — fetchMenusFromDB 가 storeInfo 를 참조하는
  //   closure 인데 deps 비어있으면 첫 시점(pos_type=undefined) 값이 freeze 되어
  //   wowpos 매장에서 빈 응답 받음. pos_type 가 채워지면 새 callback 생성.
  const handleMenuEditClose = useCallback(async () => {
    setIsMenuEditOpen(false);
    setSelectedMenuForEdit(null);
    await fetchMenusFromDB(true);
  }, [storeInfo?.pos_type]);

  const handleCategoryEditClose = useCallback(() => {
    setIsCategoryEditOpen(false);
  }, []);

  const handleCallSettingsClose = useCallback(() => {
    setIsCallSettingsOpen(false);
  }, []);

  const handleReviewSettingsClose = useCallback(() => {
    setIsReviewSettingsOpen(false);
  }, []);

  // 메뉴 표시 모드 전환 (모드1 POS ↔ 모드2 커스텀)
  const handleModeChange = useCallback(async (newMode) => {
    if (newMode === menuDisplayMode || isModeSwitching) return;
    const storeId = getStoreIdFromToken();
    if (!storeId) return;
    setIsModeSwitching(true);
    try {
      await fetch(`/api/stores/${storeId}/menu-display-mode`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ mode: newMode })
      });
      setMenuDisplayMode(newMode);
      setActiveTab(0);
      await fetchMenusFromDB(true); // 캐시 바이패스 — 모드별 응답 재로드
    } catch (e) {
      console.error('[메뉴 표시 모드 전환] 실패:', e);
      alert('모드 전환에 실패했습니다.');
    } finally {
      setIsModeSwitching(false);
    }
  }, [menuDisplayMode, isModeSwitching]); // eslint-disable-line react-hooks/exhaustive-deps

  // 영업시간 저장 (오픈~마감) — 전 POS 공통
  const handleBusinessHoursChange = useCallback(async (openTime, closeTime) => {
    const storeId = getStoreIdFromToken();
    if (!storeId) return;
    // 낙관적 갱신
    setBusinessOpenTime(openTime || '');
    setBusinessCloseTime(closeTime || '');
    try {
      const res = await fetch(`/api/wowpos/stores/${storeId}/business-hours`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ openTime: openTime || '', closeTime: closeTime || '' })
      });
      const data = await res.json();
      if (!data.success) {
        console.error('[WowPos 영업시간 저장] 실패:', data.error);
        alert(data.error || '영업시간 저장에 실패했습니다.');
      }
    } catch (e) {
      console.error('[WowPos 영업시간 저장] 실패:', e);
      alert('영업시간 저장에 실패했습니다.');
    }
  }, []);

  const handleMenuAddClose = useCallback(() => {
    setIsMenuAddOpen(false);
  }, []);

  const handleStoreSettingsSave = useCallback((updatedData) => {
    if (updatedData?.error) {
      const items = updatedData.errorItems;
      const msg = items && items.length > 0
        ? `${items.join(', ')} 저장에 실패했어요`
        : '저장에 실패했어요. 다시 시도해주세요';
      setSaveToastError(msg);
      setShowSaveToast(true);
      setTimeout(() => {
        setShowSaveToast(false);
        setSaveToastError(false);
      }, 2000);
      return;
    }
    if (updatedData) {
      setStoreInfo(prev => ({ ...prev, ...updatedData }));
    }
    // 자동 저장(silent)은 토스트 노출 X
    if (updatedData?.silent) return;
    setSaveToastError(false);
    setShowSaveToast(true);
    setTimeout(() => {
      setShowSaveToast(false);
    }, 2000);
  }, []);

  return (
    <>

      {isSyncing && (
        <div className="sync-overlay">
          <div className="sync-overlay-content">
            <svg className="sync-overlay-spinner" width="40" height="40" viewBox="0 0 40 40" fill="none">
              <circle cx="20" cy="20" r="17" stroke="rgba(255,255,255,0.2)" strokeWidth="3" fill="none"/>
              <path d="M20 3 A17 17 0 0 1 37 20" stroke="white" strokeWidth="3" strokeLinecap="round" fill="none"/>
            </svg>
            <span className="sync-overlay-ment">{syncMents[syncMentIndex]}</span>
          </div>
        </div>
      )}


      <style jsx="true">{`

          @font-face {
            font-family: 'Pretendard';
            src: url('https://woworderbucket.s3.ap-northeast-2.amazonaws.com/fonts/PretendardVariable.woff2') format('woff2-variations'),
            font-display: swap;
            font-weight: 100 900; /* Variable font 범위 */
          }

        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
          font-family: 'Pretendard', -apple-system, BlinkMacSystemFont, system-ui, Roboto, "Helvetica Neue", "Segoe UI", "Apple SD Gothic Neo", "Noto Sans KR", "Malgun Gothic", sans-serif;
          -webkit-tap-highlight-color: transparent;
          -webkit-text-size-adjust:none;
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
          text-rendering: optimizeLegibility;
        }
        
        body {
          background-color: #ffffff;
        }
        
        .scale-wrapper {
          width: 360px;
          transform-origin: top center;
          margin: 0 auto;
          position: relative;
        }
        
        @media screen and (min-width: 361px) {
          .scale-wrapper {
            zoom: calc(100vw / 360);
          }
        }
        
        @media screen and (min-width: 540px) {
          .scale-wrapper {
            zoom: 1.5;
          }
        }

        .top-container {
          width: 360px;
          height: 229px;
          position: relative;
          overflow: hidden;
        }
        
        .image-slider {
          width: 100%;
          height: 100%;
          position: relative;
          overflow: hidden;
          cursor: grab;
          user-select: none;
        }
        
        .image-slider.dragging {
          cursor: grabbing;
        }
        
        .slider-image {
          background-size: cover;
          background-position: center;
          background-repeat: no-repeat;
          background-color: #f0f0f0;
          pointer-events: none;
          will-change: transform;
        }

        .slider-image-placeholder {
          background: #F5F5F5;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 10px;
          color: #AAAAAA;
          font-size: 13px;
          font-weight: 500;
          text-align: center;
          width: 100%;
          height: 100%;
          will-change: transform;
        }

        .image-loading {
          height: 100%;
          background-color: #f0f0f0;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          color: #666;
          font-size: 12px;
        }

        .banner-pagination-dots {
          display: none;
          gap: 4px;
          position: absolute;
          left: 50%;
          transform: translateX(-50%);
          bottom: 8px;
          z-index: 2;
        }

        .banner-dot {
          width: 6px;
          height: 6px;
          min-width: 6px;
          min-height: 6px;
          max-width: 6px;
          max-height: 6px;
          border-radius: 50%;
          background-color: rgba(255, 255, 255, 0.5);
          cursor: pointer;
          transition: background-color 0.3s ease;
          border: none;
          outline: none;
          padding: 0;
          margin: 0;
          box-sizing: border-box;
        }

        .banner-dot.active {
          background-color: rgba(255, 255, 255, 1);
        }

        /* 언어 선택기 스타일 - 오른쪽 고정 */
        .language-selector {
          position: absolute;
          top: 10px;
          right: 10px;
          height: 35px;
          border-radius: 12px;
          background-color: rgba(255, 255, 255, 0.8);
          box-shadow: 0 0 4px rgba(0, 0, 0, 0.25);
          overflow: hidden;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: flex-end;
          z-index: 3;
          transition: width 0.3s cubic-bezier(0.4, 0.0, 0.2, 1);
        }
        
        .language-selector.collapsed {
          width: 65px;
          padding: 0 10px;
        }
        
        .language-selector.expanded {
          width: 236px;
          padding: 0 10px;
        }
        
        /* 현재 선택된 언어 - 항상 오른쪽 끝에 고정 */
        .current-language {
          display: flex;
          align-items: center;
          gap: 6px;
          height: 100%;
          flex-shrink: 0;
        }
        
        .current-flag {
          width: 20px;
          height: 20px;
          object-fit: cover;
          border-radius: 2px;
        }
        
        .current-text {
          font-size: 14px;
          font-weight: 600;
          color: #000000;
        }
        
        /* 축소 상태에서는 코드만 표시 */
        .language-selector.collapsed .current-text {
          text-transform: uppercase;
        }
        
        /* 확장 상태에서는 전체 이름 표시 */
        .language-selector.expanded .current-text {
        }
        
        /* 다른 언어 옵션들 컨테이너 */
        .other-languages {
          display: flex;
          align-items: center;
          height: 100%;
          gap: 12px;
          margin-right: 12px;
          overflow: hidden;
        }
        
        .language-selector.collapsed .other-languages {
          display: none;
        }
        
        .language-item {
          display: flex;
          align-items: center;
          gap: 6px;
          cursor: pointer;
          flex-shrink: 0;
          opacity: 0;
          transform: translateX(20px);
        }
        
        /* 확장될 때만 슬라이드 + 페이드인 - 오른쪽(마지막)부터 순차적으로 */
        .language-selector.expanded .language-item {
          animation: slideInFromRight 0.3s cubic-bezier(0.4, 0.0, 0.2, 1) forwards;
        }
        
        .language-selector.expanded .language-item:nth-last-child(1) {
          animation-delay: 0.075s;
        }
        
        .language-selector.expanded .language-item:nth-last-child(2) {
          animation-delay: 0.15s;
        }
        
        .language-selector.expanded .language-item:nth-last-child(3) {
          animation-delay: 0.225s;
        }
        
        .sync-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.7);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 10001;
          animation: syncFadeIn 0.3s ease;
          overflow: hidden;
          touch-action: none;
          overscroll-behavior: none;
        }

        .sync-overlay-content {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 20px;
        }

        .sync-overlay-spinner {
          animation: syncSpin 1s linear infinite;
        }

        .sync-overlay-ment {
          font-size: 16px;
          font-weight: 600;
          color: white;
          text-align: center;
          animation: syncMentFade 0.5s ease;
        }

        @keyframes syncFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes syncSpin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        @keyframes syncMentFade {
          from { opacity: 0; transform: translateY(5px); }
          to { opacity: 1; transform: translateY(0); }
        }

        @keyframes slideInFromRight {
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        .language-flag {
          width: 20px;
          height: 20px;
          object-fit: cover;
          border-radius: 2px;
        }
        
        .language-name {
          font-size: 14px;
          font-weight: 400;
          color: #666666;
          transition: color 0.2s ease;
        }
        
        .language-item:hover .language-name {
          color: #333333;
        }

        .main-container {
          width: 360px;
          position: relative;
        }

        .store-info-container {
          padding: 16px 16px 4px 16px;
          width: 360px;
          box-sizing: border-box;
        }

        .store-info {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          width: 328px;
          min-height: 31px;
          margin-bottom: 20px;
        }

        .store-name {
          flex: 1;
          min-width: 0;
          font-size: 26px;
          font-weight: 700;
          color: #000000;
          line-height: 31px;
          letter-spacing: 0;
          word-break: break-all;
        }
        /* 매장명 길이별 폰트 축소 (10자+ / 15자+) */
        .store-name.long { font-size: 23px; line-height: 27px; }
        .store-name.xlong { font-size: 16px; line-height: 20px; }

        .table-badge {
          padding: 0 5px;
          width: auto;
          height: 24px;
          background-color: #f1f1f1;
          color: #959595;
          border-radius: 6px;
          font-size: 12px;
          font-weight: 400;
          display: flex;
          align-items: center;
          justify-content: center;
          line-height: 14px;
        }

        .action-buttons {
          display: flex;
          gap: 13px;
          width: 328px;
          height: 62px;
          margin-bottom: 0px;
        }

        .action-btn {
          width: 62px;
          height: 62px;
          background-color: #ffffff;
          border-radius: 20px;
          box-shadow: 0 0px 8px rgba(0, 0, 0, 0.06);
          display: flex;
          flex-direction: column;
          align-items: center;
          padding-top: 6px;
          border: none;
          cursor: pointer;
          text-decoration: none;
          color: inherit;
          transition: transform 0.12s cubic-bezier(0.25, 0.46, 0.45, 0.94), box-shadow 0.12s ease;
          transform-origin: center;
        }
        
        .action-btn:active {
          transform: scale(0.92);
        }
        
        .store-settings-btn {
          padding-top: 7px;
        }

        .action-btn img {
          width: 34px;
          height: 34px;
        }

        .action-btn span {
          font-size: 9px;
          font-weight: 500;
          color: #000000;
          line-height: 11px;
          text-align: center;
        }

        .live-status {
          width: 180px;
          height: 62px;
          position: relative;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: transform 0.2s cubic-bezier(0.4, 0, 0.2, 1);
        }
        
        .live-status:active {
          transform: scale(0.95);
        }
        
        .live-status-content {
          position: absolute;
          display: flex;
          flex-direction: column;
          align-items: center;
          z-index: 1;
        }
        
        .live-status-content img {
          width: 23px;
          height: 23px;
          margin-bottom: 4px;
        }
        
        .live-status-content span {
          font-size: 10px;
          font-weight: 700;
          text-align: center;
          line-height: 12px;
        }

        .notice-container {
          width: 360px;
          padding: 15px 16px 16px 16px;
          margin: 0;
          box-sizing: border-box;
          border-bottom: 8px solid #f3f5f7;
        }

        .notice {
          width: 328px;
          min-height: 32px;
          height: 32px;
          padding: 8px 8px 8px 12px;
          border: 1px solid #d2d2d2;
          border-radius: 8px;
          background-color: #ffffff;
          display: flex;
          align-items: flex-start;
          position: relative;
          cursor: pointer;
          transition: height 0.2s cubic-bezier(0.4, 0, 0.2, 1), border-color 0.2s ease;
          overflow: visible;
        }
        
        .notice.expanded {
          height: auto !important;
          min-height: 32px;
        }

        .notice-content {
          display: flex;
          align-items: flex-start;
          gap: 4px;
          width: calc(100% - 20px);
        }
        
        .notice-toggle-icon {
          position: absolute;
          right: 8px;
          top: 7px;
          width: 13px;
          height: 13px;
          cursor: pointer;
        }

        .notice-icon {
          width: 14px;
          height: 14px;
          position: relative;
          flex-shrink: 0;
          margin-top: 0;
        }
        
        .notice-icon.disabled {
          filter: grayscale(100%) opacity(0.5);
        }
        
        .notice-text.disabled {
          color: #959595;
          text-decoration: line-through;
        }

        .notice-text {
          flex: 1;
          font-size: 11px;
          font-weight: 500;
          color: #000000;
          line-height: 15px;
          letter-spacing: 0;
          overflow: hidden;
          text-overflow: ellipsis;
          display: -webkit-box;
          -webkit-line-clamp: 1;
          line-clamp: 1;
          -webkit-box-orient: vertical;
          cursor: text;
        }
        
        .notice.expanded .notice-text {
          -webkit-line-clamp: unset;
          line-clamp: unset;
          white-space: normal;
          max-height: none;
        }

        /* Notice Modal Styles */
        .notice-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(0, 0, 0, 0.3);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 9999;
          overflow: hidden;
          touch-action: none;
          -webkit-overflow-scrolling: none;
          overscroll-behavior: none;
        }

        .notice-modal {
          width: 320px;
          background-color: #ffffff;
          border-radius: 16px;
          padding: 32px 26px 24px 26px;
          box-sizing: border-box;
          touch-action: none;
        }

        .notice-modal-content {
          width: 268px;
        }

        .notice-modal-title {
          font-size: 20px;
          font-weight: 700;
          color: #000000;
          padding-left: 4px;
          margin-bottom: 12px;
        }

        .notice-modal-input {
          width: 268px;
          min-height: 89px;
          border-radius: 6px;
          border: 1px solid #E8E8E8;
          padding: 14px;
          box-sizing: border-box;
          font-size: 16px;
          font-weight: 600;
          color: #000000;
          resize: none;
          outline: none;
          font-family: inherit;
          line-height: 1.4;
          caret-color: #FDCD27;
          overflow-y: auto;
        }

        .notice-modal-input::-webkit-scrollbar {
          width: 4px;
        }

        .notice-modal-input::-webkit-scrollbar-track {
          background: transparent;
          margin: 6px 0;
        }

        .notice-modal-input::-webkit-scrollbar-thumb {
          background: #D9D9D9;
          border-radius: 2px;
        }

        .notice-modal-input:focus {
          border-color: #FDCD27;
        }

        .notice-modal-input::placeholder {
          font-size: 16px;
          font-weight: 500;
          color: #CBCBCB;
        }

        .notice-modal-buttons {
          display: flex;
          gap: 8px;
          margin-top: 24px;
        }

        .notice-modal-btn {
          width: 130px;
          height: 46px;
          border-radius: 8px;
          border: none;
          padding: 14px;
          font-size: 15px;
          cursor: pointer;
          display: flex;
          justify-content: center;
          align-items: center;
        }

        .notice-modal-btn-cancel {
          background-color: #F1F1F1;
          font-weight: 600;
          color: #959595;
        }

        .notice-modal-btn-save {
          background-color: #FDCD27;
          font-weight: 900;
          color: #FFFFFF;
        }


        .category-container {
          position: sticky;
          position: -webkit-sticky;
          top: 0;
          width: 360px;
          height: 51px;
          background-color: #ffffff;
          z-index: 1000;
          transition: all 0.0375s ease;
          transform: translateZ(0);
          -webkit-transform: translateZ(0);
          will-change: transform;
          margin: 0 auto;
          left: 0;
          right: 0;
        }
        
        .category-edit-button {
          position: absolute;
          right: 0px;
          top: 0;
          bottom: 0;
          width: 50px;
          height: 100%;
          border: none;
          background: none;
          padding: 0;
          cursor: pointer;
          display: flex;
          justify-content: center;
          overflow: hidden;
          z-index: 10;
        }
        
        .category-edit-blur {
          position: absolute;
          top: 0;
          width: 100px;
          height: 98%;
          object-fit: cover;
        }
        
        .category-edit-icon {
          position: relative;
          z-index: 1;
              top: 13px;
          width: 22px;
          height: 22px;
        }
        
        .category-container::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 0;
          width: 100%;
          height: 1.2px;
          background-color: #f3f5f7;
        }
        
        .category-tabs {
          display: flex;
          gap: 22px;
          padding: 16px 66px 16px 16px;
          height: 50px;
          overflow-x: auto;
          scrollbar-width: none;
          -ms-overflow-style: none;
          align-items: center;
          -webkit-overflow-scrolling: touch;
          scroll-behavior: smooth;
          position: relative;
        }
        
        .category-tabs.blocked {
          pointer-events: none;
        }

        .category-tabs::-webkit-scrollbar {
          display: none;
        }

        .category-tab {
          font-size: 15px;
          font-weight: 500;
          color: #797979;
          white-space: nowrap;
          cursor: pointer;
          transition: all 0.025s ease;
          height: 17px;
          line-height: 17px;
          letter-spacing: 0;
          -webkit-tap-highlight-color: transparent;
          user-select: none;
        }
        
        .category-tab.disabled {
          cursor: not-allowed;
        }

        .category-tab.active {
          font-size: 16px;
          font-weight: 600;
          color: #000000;
          height: 18px;
          line-height: 18px;
          animation: tabBounce 0.3s ease-out;
        }
        
        @keyframes tabBounce {
          0% {
            transform: scale(1);
          }
          50% {
            transform: scale(1.1);
          }
          100% {
            transform: scale(1);
          }
        }

        .category-highlight {
          position: absolute;
          bottom: 0px;
          left: 0;
          width: 0;
          height: 2px;
          background-color: #000000;
          border-radius: 10px;
          transition: all 0.0375s ease;
          z-index: 1;
          pointer-events: none;
        }

        .menu-display-container {
          width: 360px;
          position: relative;
          padding-top: 6px;
        }

        .menu-container {
          width: 360px;
          position: relative;
          padding-top: 4px;
        }
        
        .add-menu-button {
          width: 100%;
          padding: 14px 0;
          background-color: transparent;
          border: none;
          font-size: 16px;
          font-weight: 600;
          color: #959595;
          text-align: center;
          cursor: pointer;
          position: relative;
          overflow: hidden;
        }
        
        .add-menu-button.empty-category {
          border-top: 1px solid #f3f5f7;
        }
        
        .menu-container:not(:last-child)::after {
          content: '';
          display: block;
          width: 100%;
          height: 10px;
          background-color: #f3f5f7;
        }
        
        .menu-container:last-child {
          margin-bottom: 0;
        }

        .menu-container:first-child {
          padding-top: 0;
        }

        .category-badge-container {
          padding: 18px 16px 4px 20px;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .scale-wrapper .category-badge {
          background-color: #f1f1f1;
          color: #000000;
          padding: 4px 8px;
          border-radius: 8px;
          font-size: 16px;
          font-weight: 500;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          width: auto;
          height: auto;
          line-height: 19px;
          text-align: center;
          white-space: nowrap;
          box-sizing: border-box;
          contain: layout style;
          cursor: pointer;
          user-select: none;
          -webkit-tap-highlight-color: transparent;
          transition: transform 0.12s ease, background-color 0.12s ease;
        }

        .scale-wrapper .category-badge:active {
          background-color: #e8e8e8;
          transform: scale(0.96);
        }

        .category-badge-edit-icon {
          flex-shrink: 0;
        }

        /* + 추가 버튼 — 카테고리 뱃지 우측 (Step 1) */
        .category-add-btn {
          background-color: #f1f1f1;
          color: #2f2f2f;
          border: none;
          border-radius: 8px;
          width: 28px;
          height: 28px;
          font-size: 20px;
          font-weight: 500;
          line-height: 1;
          cursor: pointer;
          padding: 0 0 2px 0;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          user-select: none;
          -webkit-tap-highlight-color: transparent;
          transition: transform 0.12s ease, background-color 0.12s ease;
        }
        .category-add-btn:active {
          background-color: #e8e8e8;
          transform: scale(0.94);
        }

        .menu-item {
          display: flex;
          flex-direction: column;
          padding: 16px;
          width: 360px;
          border-bottom: 1px solid #f3f5f7;
          transition: padding 0.3s ease, background-color 0.3s ease, transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          position: relative;
          overflow: hidden;
          -webkit-touch-callout: none;
          -webkit-user-select: none;
          -khtml-user-select: none;
          -moz-user-select: none;
          -ms-user-select: none;
          user-select: none;
          touch-action: manipulation;
          -webkit-tap-highlight-color: transparent;
        }


        .menu-item.dragging-mode {
          cursor: grabbing;
          overflow: visible;
        }

        /* 드래그 중일 때 해당 카테고리의 모든 메뉴 아이템 설정 */
        .menu-container.dragging .menu-item {
          overflow: visible;
        }

        /* 드래그 중일 때 padding transition 비활성화 (높이 변화 방지) */
        .menu-container.dragging .menu-item {
          transition: background-color 0.3s ease, transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }
        
        
        .ripple {
          position: absolute;
          border-radius: 50%;
          background-color: rgba(0, 0, 0, 0.1);
          transform: scale(0);
          opacity: 0;
          transform-origin: center center;
          pointer-events: none;
        }
        
        .ripple.expand {
          animation: rippleExpand 0.4s cubic-bezier(0.4, 0, 0.2, 1) forwards;
        }
        
        @keyframes rippleExpand {
          0% {
            transform: scale(0.4);
            opacity: 0.8;
          }
          30% {
            opacity: 0.7;
          }
          100% {
            transform: scale(2.5);
            opacity: 0;
          }
        }
        
        
        .menu-container .menu-item:last-child {
          border-bottom: none;
        }
        
        .menu-item-basic {
          display: flex;
          justify-content: space-between;
          width: 100%;
          -webkit-user-select: none;
          -moz-user-select: none;
          -ms-user-select: none;
          user-select: none;
        }

        .menu-info {
          width: 234px;
          min-height: 51px;
          padding-top: 4px;
          height: auto;
          display: flex;
          flex-direction: column;
        }

        .menu-tags {
          display: flex;
          align-items: center;
          gap: 6px;
          width: auto;
          min-height: 18px;
          margin-bottom: 6px;
        }

        .menu-tags:empty {
          margin-bottom: 0;
          min-height: 0;
        }
        
        .menu-tag {
          height: 18px;
          width: auto;
        }

        .menu-tag-most-ordered {
          font-size: 12px;
          font-weight: 700;
          line-height: 14px;
          letter-spacing: 0;
          color: #FDCD27;
          white-space: nowrap;
        }

        .menu-name {
          width: 206px;
          height: 19px;
          font-size: 16px;
          font-weight: 600;
          color: #000000;
          margin-bottom: 6px;
          line-height: 19px;
          letter-spacing: 0;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .menu-description {
          width: 206px;
          font-size: 12px;
          font-weight: 500;
          color: #949494;
          line-height: 18px;
          margin-bottom: 10px;
          letter-spacing: -0.24px;
          word-break: break-word;
        }

        .menu-price {
          width: 206px;
          height: 18px;
          font-size: 15px;
          font-weight: 700;
          color: #000000;
          line-height: 18px;
          letter-spacing: 0;
        }

        .admin-menu-image-container {
          position: relative;
          width: 94px;
          height: 94px;
          -webkit-user-select: none;
          -moz-user-select: none;
          -ms-user-select: none;
          user-select: none;
          overflow: hidden;
          border-radius: 6px;
          border: 1.2px solid #EEEEEE;
          box-sizing: border-box;
        }
        .admin-menu-image-container.no-image {
          background: transparent;
          width: 0;
          height: 0;
          overflow: visible;
          position: static;
        }
        .admin-menu-image-container.no-image .sold-out-overlay {
          background-color: transparent;
          width: 94px;
          height: 94px;
          top: auto;
          left: auto;
          right: 16px;
          bottom: 50%;
          transform: translateY(50%);
        }

        .admin-menu-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
          -webkit-user-drag: none;
          -khtml-user-drag: none;
          -moz-user-drag: none;
          -o-user-drag: none;
          user-drag: none;
        }

        .sold-out-overlay {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background-color: rgba(255, 255, 255, 0.7);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1;
        }

        .sold-out-badge {
          background-color: hsla(0, 0.00%, 0.00%, 0.40);
          color: #ffffff;
          padding: 3px 8px;
          min-width: 37px;
          height: auto;
          border-radius: 7px;
          font-size: 12px;
          font-weight: 700;
          display: flex;
          align-items: center;
          justify-content: center;
          line-height: 14px;
          white-space: nowrap;
        }
        
        .time-limit-badge {
          background-color: hsla(0, 0.00%, 0.00%, 0.40);
          color: #ffffff;
          padding: 3px 6px;
          border-radius: 7px;
          font-size: 12px;
          font-weight: 700;
          display: flex;
          align-items: center;
          justify-content: center;
          text-align: center;
          white-space: normal;
          max-width: 86px;
          line-height: 14px;
          letter-spacing: -0.02em;
        }

        .menu-item.sold-out .menu-name,
        .menu-item.sold-out .menu-description,
        .menu-item.sold-out .menu-price {
          color: #C4C4C4;
        }
        
        .menu-item.time-limited .menu-name,
        .menu-item.time-limited .menu-description,
        .menu-item.time-limited .menu-price {
          color: #C4C4C4;
        }

        /* 옵션 관련 스타일 */
        .menu-item {
          position: relative;
        }
        

        .white-spacer {
          width: 360px;
          height: 10px;
          background-color: #ffffff;
        }

        .bottom-info {
          background-color: #f3f5f7;
          padding: 30px 16px 160px;
          width: 360px;
          min-height: 374px;
        }

        .info-section {
          width: 328px;
          margin-bottom: 20px;
        }

        .info-title {
          width: 328px;
          height: 16px;
          font-size: 13px;
          font-weight: 700;
          color: #6e6e6e;
          margin-bottom: 6px;
          line-height: 16px;
          letter-spacing: 0;
        }

        .info-content {
          width: 328px;
          font-size: 13px;
          font-weight: 400;
          color: #949494;
          line-height: 20px;
          letter-spacing: 0;
        }

        .fixed-bottom {
          background-image: url(${bottomNavIcon});
          position: fixed;
          bottom: 0;
          left: 50%;
          transform: translateX(-50%);
          width: 360px;
          height: 100px;
          display: flex;
          justify-content: center;
          align-items: flex-end;
          z-index: 1000;
          pointer-events: none; /* 전체 영역 클릭 불가 */
        }

        .bottom-button {
          position: absolute;
          bottom: 0%;
          margin-bottom: 11px;
          width: 328px;
          height: 50px;
          background-color: #fdcd27;
          border-radius: 10px;
          border: none;
          font-size: 17px;
          font-weight: 700;
          color: #ffffff;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          letter-spacing: -0.32px;
          line-height: 19px;
          transition: transform 0.12s cubic-bezier(0.4, 0.0, 0.2, 1), box-shadow 0.12s ease;
          pointer-events: auto; /* 버튼만 클릭 가능 */
        }

        .bottom-button:active {
          transform: scale(0.92);
        }

        .cart-count {
          background-color: #ffffff;
          color: #fdcd27;
          width: 22px;
          height: 22px;
          border-radius: 6px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 15px;
          font-weight: 700;
          line-height: 1;
          padding: 0;
          box-sizing: border-box;
          position: relative;
          overflow: hidden;
        }
        
        .count-animation-container {
          position: relative;
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .count-digit {
          position: absolute;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 100%;
          height: 100%;
        }
        
        .count-exit-up {
          animation: countSlideUp 0.3s cubic-bezier(0.4, 0, 0.2, 1) forwards;
        }
        
        .count-exit-down {
          animation: countSlideDown 0.3s cubic-bezier(0.4, 0, 0.2, 1) forwards;
        }
        
        .count-enter-up {
          animation: countSlideInUp 0.3s cubic-bezier(0.4, 0, 0.2, 1) forwards;
        }
        
        .count-enter-down {
          animation: countSlideInDown 0.3s cubic-bezier(0.4, 0, 0.2, 1) forwards;
        }
        
        @keyframes countSlideUp {
          from {
            transform: translateY(0);
            opacity: 1;
          }
          to {
            transform: translateY(-100%);
            opacity: 0;
          }
        }
        
        @keyframes countSlideDown {
          from {
            transform: translateY(0);
            opacity: 1;
          }
          to {
            transform: translateY(100%);
            opacity: 0;
          }
        }
        
        @keyframes countSlideInUp {
          from {
            transform: translateY(100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        
        @keyframes countSlideInDown {
          from {
            transform: translateY(-100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }

        /* 룰렛 애니메이션 스타일 */
        .price-slot-container {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 0;
          overflow: hidden;
          height: 19px;
          position: relative;
          font-feature-settings: "tnum";
          font-variant-numeric: tabular-nums;
        }
        
        .digit-container {
          display: inline-flex;
          flex-direction: column;
          height: 19px;
          position: relative;
          overflow: hidden;
          min-width: 10px;
          align-items: center;
          justify-content: center;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }
        
        .digit-container.comma {
          min-width: 4px;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }
        
        .digit-container.slot-appearing {
          animation: slotAppear 0.3s cubic-bezier(0.4, 0, 0.2, 1) forwards;
        }
        
        .digit-container.slot-disappearing {
          animation: slotDisappear 0.3s cubic-bezier(0.4, 0, 0.2, 1) forwards;
          min-width: 0;
          width: 0;
          margin: 0;
        }
        
        @keyframes slotAppear {
          0% {
            width: 0;
            min-width: 0;
            opacity: 0;
            margin: 0;
          }
          100% {
            width: 10px;
            min-width: 10px;
            opacity: 1;
            margin: 0;
          }
        }
        
        @keyframes slotDisappear {
          0% {
            width: 10px;
            min-width: 10px;
            opacity: 1;
            margin: 0;
          }
          100% {
            width: 0;
            min-width: 0;
            opacity: 0;
            margin: 0;
          }
        }
        
        .digit {
          display: inline-block;
          line-height: 19px;
          transition: none;
          font-size: 17px;
          font-weight: 700;
          color: #ffffff;
          position: absolute;
          left: 50%;
          transform: translateX(-50%);
        }
        
        .digit-fade-in {
          animation: digitFadeIn 0.3s ease-out both;
        }
        
        .digit-exit {
          position: absolute;
          animation: slideOutBottom 0.3s cubic-bezier(0.4, 0, 0.2, 1) both;
        }
        
        .digit-exit-up {
          position: absolute;
          animation: slideOutTop 0.3s cubic-bezier(0.4, 0, 0.2, 1) both;
        }
        
        .digit-enter {
          animation: slideInTop 0.3s cubic-bezier(0.4, 0, 0.2, 1) both;
        }
        
        .digit-enter-up {
          animation: slideInBottom 0.3s cubic-bezier(0.4, 0, 0.2, 1) both;
        }
        
        .digit-intermediate {
          position: absolute;
          animation: slideIntermediateQuick 100ms linear both;
        }
        
        .digit-intermediate-up {
          position: absolute;
          animation: slideIntermediateQuickUp 100ms linear both;
        }
        
        @keyframes slideInTop {
          from {
            transform: translateX(-50%) translateY(-100%);
            opacity: 0;
          }
          to {
            transform: translateX(-50%) translateY(0);
            opacity: 1;
          }
        }
        
        @keyframes slideOutBottom {
          from {
            transform: translateX(-50%) translateY(0);
            opacity: 1;
          }
          to {
            transform: translateX(-50%) translateY(100%);
            opacity: 0;
          }
        }
        
        @keyframes slideInBottom {
          from {
            transform: translateX(-50%) translateY(100%);
            opacity: 0;
          }
          to {
            transform: translateX(-50%) translateY(0);
            opacity: 1;
          }
        }
        
        @keyframes slideOutTop {
          from {
            transform: translateX(-50%) translateY(0);
            opacity: 1;
          }
          to {
            transform: translateX(-50%) translateY(-100%);
            opacity: 0;
          }
        }
        
        @keyframes slideIntermediateQuick {
          0% {
            transform: translateX(-50%) translateY(-100%);
            opacity: 0.8;
          }
          50% {
            transform: translateX(-50%) translateY(0);
            opacity: 1;
          }
          100% {
            transform: translateX(-50%) translateY(100%);
            opacity: 0;
          }
        }
        
        @keyframes slideIntermediateQuickUp {
          0% {
            transform: translateX(-50%) translateY(100%);
            opacity: 0.8;
          }
          50% {
            transform: translateX(-50%) translateY(0);
            opacity: 1;
          }
          100% {
            transform: translateX(-50%) translateY(-100%);
            opacity: 0;
          }
        }
        
        @keyframes digitFadeIn {
          0% {
            opacity: 0;
            transform: translateX(-50%) scale(0.8);
          }
          100% {
            opacity: 1;
            transform: translateX(-50%) scale(1);
          }
        }
        
        .price-prefix {
          font-size: 17px;
          font-weight: 700;
          color: #ffffff;
          line-height: 19px;
          margin-right: 1px;
        }
        
        .price-suffix {
          font-size: 17px;
          font-weight: 700;
          color: #ffffff;
          line-height: 19px;
          margin-left: 2px;
        }

        .navigation-bar {
          width: 360px;
          height: 42px;
          background-image: url(${navigationBarIcon});
          background-size: cover;
          background-position: center;
          position: fixed;
          bottom: 72px;
          left: 50%;
          transform: translateX(-50%);
          z-index: 999;
        }
        
        .toast-popup {
          position: fixed;
          bottom: 80px;
          left: 50%;
          transform: translateX(-50%);
          background-color: #3C3C3C;
          color: #ffffff;
          padding: 0 16px;
          height: 42px;
          display: flex;
          align-items: center;
          border-radius: 100px;
          font-size: 14px;
          font-weight: 600;
          z-index: 10000;
          white-space: nowrap;
          animation: toastSlideUp 0.3s ease-out, toastFadeOut 0.3s ease-out 1.7s forwards;
        }
        
        @media screen and (min-width: 361px) {
          .toast-popup {
            zoom: calc(100vw / 360);
          }
        }
        
        @media screen and (min-width: 540px) {
          .toast-popup {
            zoom: 1.5;
          }
        }
        
        @keyframes toastSlideUp {
          from {
            transform: translateX(-50%) translateY(20px);
            opacity: 0;
          }
          to {
            transform: translateX(-50%) translateY(0);
            opacity: 1;
          }
        }
        
        @keyframes toastFadeOut {
          from {
            transform: translateX(-50%) translateY(0);
            opacity: 1;
          }
          to {
            transform: translateX(-50%) translateY(20px);
            opacity: 0;
          }
        }
      `}</style>
      
      <div className="scale-wrapper">
        {/* Top Container */}
        <div className="top-container">
          {/* Image Slider */}
          <div
            ref={sliderRef}
            className={`image-slider ${isDragging ? 'dragging' : ''}`}
            onMouseDown={handleMouseDown}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            onClick={() => {
              // 스와이프하지 않은 단순 클릭일 때만 수정 페이지 열기
              if (!hasDragged) {
                setIsMenuAddOpen(true);
              }
              setHasDragged(false); // 클릭 후 초기화
            }}
          >
            {!isLoadingImages && sliderImages.length === 0 ? (
              <div className="slider-image-placeholder">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#CCCCCC" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                  <circle cx="8.5" cy="8.5" r="1.5"/>
                  <polyline points="21 15 16 10 5 21"/>
                </svg>
                <span>클릭하여 배너 이미지를 추가해주세요</span>
              </div>
            ) : sliderImages.length > 0 ? (
              (() => {
                const len = sliderImages.length;
                const prevIdx = (bannerIndex - 1 + len) % len;
                const nextIdx = (bannerIndex + 1) % len;
                const w = getSliderWidth();
                const transStyle = slideTransition ? 'transform 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)' : 'none';
                const slides = len === 1
                  ? [{ idx: bannerIndex, base: 0 }]
                  : [
                      { idx: prevIdx, base: -w },
                      { idx: bannerIndex, base: 0 },
                      { idx: nextIdx, base: w }
                    ];
                const useIdxKey = len >= 3;
                return slides.map(({ idx, base }) => {
                  const isError = imageLoadErrors[idx];
                  const pos = base + slideOffset;
                  const isCurrent = base === 0;
                  const key = useIdxKey ? `banner-${idx}` : `banner-pos-${base}`;
                  if (isError) {
                    return (
                      <div
                        key={key}
                        className="slider-image-placeholder"
                        style={{
                          position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
                          transform: `translateX(${pos}px)`,
                          transition: transStyle,
                          zIndex: isCurrent ? 2 : 1
                        }}
                        onTransitionEnd={isCurrent ? handleSlideTransitionEnd : undefined}
                      >
                        이미지 {idx + 1}
                      </div>
                    );
                  }
                  if (isVideoUrl(sliderImageUrls[idx])) {
                    return (
                      <video
                        key={key}
                        ref={el => {
                          bannerVideoRefs.current[idx] = el;
                          // 마운트 직후 강제 즉시 로드 → Android Chrome의 lazy preload 우회
                          if (el && !el.dataset.eagerLoaded) {
                            el.dataset.eagerLoaded = '1';
                            try { el.load(); } catch (e) {}
                          }
                        }}
                        src={sliderImageUrls[idx]}
                        className="slider-image"
                        muted
                        playsInline
                        preload="auto"
                        autoPlay={isCurrent}
                        onEnded={() => {
                          if (bannerIndexRef.current === idx) goToNext();
                        }}
                        onLoadedData={async (e) => {
                          // 비활성 비디오: 첫 프레임 강제 페인트를 위해 짧게 play→pause (currentTime=0 단독으론 모바일에서 미동작)
                          if (bannerIndexRef.current !== idx) {
                            const v = e.target;
                            try {
                              await v.play();
                              v.pause();
                              v.currentTime = 0;
                            } catch (err) {}
                          }
                        }}
                        onError={() => setImageLoadErrors(prev => ({ ...prev, [idx]: true }))}
                        style={{
                          position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
                          objectFit: 'cover',
                          transform: `translateX(${pos}px)`,
                          transition: transStyle,
                          zIndex: isCurrent ? 2 : 1
                        }}
                        onTransitionEnd={isCurrent ? handleSlideTransitionEnd : undefined}
                      />
                    );
                  }
                  return (
                    <div
                      key={key}
                      className="slider-image"
                      style={{
                        position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
                        backgroundImage: `url(${sliderImageUrls[idx]})`,
                        transform: `translateX(${pos}px)`,
                        transition: transStyle,
                        zIndex: isCurrent ? 2 : 1
                      }}
                      onTransitionEnd={isCurrent ? handleSlideTransitionEnd : undefined}
                    />
                  );
                });
              })()
            ) : null}
          </div>

          <div className="banner-pagination-dots">
            {sliderImages.map((_, index) => (
              <div
                key={index}
                className={`banner-dot ${bannerIndex === index ? 'active' : ''}`}
                onClick={() => {
                  goToSlide(index);
                  stopAutoPlay();
                  if (sliderImages.length > 1) {
                    setTimeout(() => startAutoPlay(), 2500);
                  }
                }}
              />
            ))}
          </div>
          
          {/* 언어 선택기 */}
          <div
            className={`language-selector ${isLanguageExpanded ? 'expanded' : 'collapsed'}`}
            onClick={() => setIsLanguageExpanded(!isLanguageExpanded)}
          >
            {isLanguageExpanded && (
              <div className="other-languages">
                {languages
                  .filter(lang => lang.code !== selectedLanguage)
                  .map((lang) => (
                    <div
                      key={lang.code}
                      className="language-item"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (navigator.vibrate) {
                          navigator.vibrate(30);
                        }
                        setSelectedLanguage(lang.code);
                        localStorage.setItem('adminLanguage', lang.code);
                        setIsLanguageExpanded(false);
                      }}
                    >
                      <img src={lang.flag} alt={lang.name} className="language-flag" />
                      <span className="language-name">{lang.name}</span>
                    </div>
                  ))
                }
              </div>
            )}

            <div className="current-language">
              <img
                src={languages.find(l => l.code === selectedLanguage)?.flag}
                alt={selectedLanguage}
                className="current-flag"
              />
              <span className="current-text">
                {isLanguageExpanded
                  ? languages.find(l => l.code === selectedLanguage)?.name
                  : selectedLanguage === 'zh' ? 'CN' : selectedLanguage.toUpperCase()
                }
              </span>
            </div>
          </div>
        </div>

        {/* Main Container */}
        <div className="main-container">
          {/* Store Info Container */}
          <div className="store-info-container">
            <div className="store-info">
              <span className={`store-name${(storeInfo.storeName?.length || 0) >= 15 ? ' xlong' : (storeInfo.storeName?.length || 0) >= 10 ? ' long' : ''}`}>{storeInfo.storeName}</span>
              <div
                className="table-badge"
                onClick={() => setIsOrderOverviewOpen(true)}
                style={{ cursor: 'pointer' }}
              >
                관리자 테이블
              </div>
            </div>

            {/* Action Buttons */}
            <div className="action-buttons">
              <button className="action-btn" onClick={() => setIsCallSettingsOpen(true)}>
                <img src={callbellIcon} alt="호출 설정" />
                <span>호출 설정</span>
              </button>
              
              <div className="live-status" style={{ filter: 'drop-shadow(0px 0px 11px rgba(253, 205, 40, 0.28))' }} onClick={() => setIsReviewSettingsOpen(true)}>
                <svg width="180" height="64" viewBox="-1 -1 180 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                  {/* 흰색 배경 (그림자가 내부로 침투하지 않도록) */}
                  <path d="M0 31C0 16.8854 0 9.82812 4.14478 5.25506C4.49648 4.86702 4.86702 4.49648 5.25506 4.14478C9.82812 0 16.8854 0 31 0H147C161.115 0 168.172 0 172.745 4.14478C173.133 4.49648 173.504 4.86702 173.855 5.25506C178 9.82812 178 16.8854 178 31V31C178 45.1146 178 52.1719 173.855 56.7449C173.504 57.133 173.133 57.5035 172.745 57.8552C168.172 62 161.115 62 147 62H31C16.8854 62 9.82812 62 5.25506 57.8552C4.86702 57.5035 4.49648 57.133 4.14478 56.7449C0 52.1719 0 45.1146 0 31V31Z"
                        fill="#ffffff"/>

                  {/* 내부 배경 (옅은 노란 그라데이션) */}
                  <path d="M0 31C0 16.8854 0 9.82812 4.14478 5.25506C4.49648 4.86702 4.86702 4.49648 5.25506 4.14478C9.82812 0 16.8854 0 31 0H147C161.115 0 168.172 0 172.745 4.14478C173.133 4.49648 173.504 4.86702 173.855 5.25506C178 9.82812 178 16.8854 178 31V31C178 45.1146 178 52.1719 173.855 56.7449C173.504 57.133 173.133 57.5035 172.745 57.8552C168.172 62 161.115 62 147 62H31C16.8854 62 9.82812 62 5.25506 57.8552C4.86702 57.5035 4.49648 57.133 4.14478 56.7449C0 52.1719 0 45.1146 0 31V31Z"
                        fill="url(#inner_gradient_small)"/>

                  {/* 외곽선 (진한 노란 그라데이션) - 바깥쪽에 그리기 */}
                  <path d="M0 31C0 16.8854 0 9.82812 4.14478 5.25506C4.49648 4.86702 4.86702 4.49648 5.25506 4.14478C9.82812 0 16.8854 0 31 0H147C161.115 0 168.172 0 172.745 4.14478C173.133 4.49648 173.504 4.86702 173.855 5.25506C178 9.82812 178 16.8854 178 31V31C178 45.1146 178 52.1719 173.855 56.7449C173.504 57.133 173.133 57.5035 172.745 57.8552C168.172 62 161.115 62 147 62H31C16.8854 62 9.82812 62 5.25506 57.8552C4.86702 57.5035 4.49648 57.133 4.14478 56.7449C0 52.1719 0 45.1146 0 31V31Z"
                        fill="none"
                        stroke="url(#border_gradient_small)"
                        strokeWidth="1"/>

                  <defs>
                    {/* 내부 배경용 옅은 노란 그라데이션 */}
                    <linearGradient id="inner_gradient_small" x1="0%" y1="0%" x2="100%" y2="100%" gradientUnits="userSpaceOnUse">
                      <stop offset="0%" stopColor="#FDE68A" stopOpacity="0.18"/>
                      <stop offset="50%" stopColor="#FDCD28" stopOpacity="0.18"/>
                      <stop offset="100%" stopColor="#F5B800" stopOpacity="0.18"/>
                    </linearGradient>

                    {/* 외곽선용 진한 노란 그라데이션 */}
                    <linearGradient id="border_gradient_small" x1="0%" y1="0%" x2="100%" y2="100%" gradientUnits="userSpaceOnUse">
                      <stop offset="0%" stopColor="#FDE68A" stopOpacity="1"/>
                      <stop offset="50%" stopColor="#FDCD28" stopOpacity="1"/>
                      <stop offset="100%" stopColor="#F5B800" stopOpacity="1"/>
                    </linearGradient>
                  </defs>
                </svg>
                <div className="live-status-content">
                  <div style={{ display: 'flex', marginBottom: '5px' }}>
                    {[0, 1, 2, 3, 4].map((i) => (
                      <svg key={i} width="16" height="16" viewBox="0 0 24 24" fill="#FDCD28" xmlns="http://www.w3.org/2000/svg" style={{ marginLeft: i === 0 ? 0 : '-1.5px' }}>
                        <path d="M12 2l2.9 6.26 6.86.62-5.18 4.52 1.55 6.72L12 17.3 5.87 20.64l1.55-6.72L2.24 8.88l6.86-.62L12 2z"/>
                      </svg>
                    ))}
                  </div>
                  <span style={{ fontSize: '10px', fontWeight: '700', color: '#C79200' }}>리뷰 설정</span>
                </div>
              </div>
              
              <button 
                className="action-btn store-settings-btn"
                onClick={() => setIsStoreSettingsOpen(true)}
              >
                <img src={storeinfoIcon} alt="매장 설정" />
                <span>매장 설정</span>
              </button>
            </div>
          </div>

          {/* Notice */}
          <div className="notice-container">
            <div
              ref={noticeRef}
              className="notice expanded"
              onClick={() => {
                setTempNoticeText(noticeText[selectedLanguage] || '');
                setIsNoticeModalOpen(true);
              }}
            >
              <div className="notice-content" ref={noticeContentRef}>

                <img
                  className={`notice-icon ${!isNoticeEnabled ? 'disabled' : ''}`}
                  src={noticeIcon}
                  alt="공지"
                  style={{ width: '14px', height: '14px' }}
                />

                <div
                  className={`notice-text ${!isNoticeEnabled ? 'disabled' : ''}`}
                >
                  {noticeText[selectedLanguage] || (
                    <span style={{ color: '#BEBEBE' }}>공지사항을 입력하는 곳이에요</span>
                  )}
                </div>
              </div>
              <img
                className="notice-toggle-icon"
                src={isNoticeEnabled
                  ? noticeOnIcon
                  : noticeOffIcon
                }
                alt="공지 토글"
                style={{ width: '13px', height: '13px' }}
                onClick={(e) => {
                  e.stopPropagation();
                  if (navigator.vibrate) {
                    navigator.vibrate(30);
                  }
                  const newEnabledState = !isNoticeEnabled;
                  setIsNoticeEnabled(newEnabledState);

                  const storeId = getStoreIdFromToken() || 1;

                  fetch(`/api/notices/${storeId}`, {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json',
                      ...authHeaders()
                    },
                    body: JSON.stringify({
                      texts: noticeText,
                      enabled: newEnabledState
                    })
                  }).then(response => response.json())
                    .then(data => {
                      if (data.success) {
                        localStorage.setItem('noticeUpdated', Date.now().toString());
                        window.dispatchEvent(new StorageEvent('storage', {
                          key: 'noticeUpdated',
                          newValue: Date.now().toString(),
                          url: window.location.href
                        }));
                      }
                    })
                    .catch(error => console.error('Error toggling notice:', error));
                }}
              />
            </div>
          </div>

          {/* Notice Modal */}
          {isNoticeModalOpen && (
            <div
              className="notice-modal-overlay"
              style={keyboardHeight > 0 ? { alignItems: 'flex-end' } : {}}
              onClick={() => setIsNoticeModalOpen(false)}
              onTouchMove={(e) => e.preventDefault()}
            >
              <div
                className="notice-modal"
                style={keyboardHeight > 0 ? { marginBottom: keyboardHeight + 12 } : {}}
                onClick={(e) => e.stopPropagation()}
                onTouchMove={(e) => e.stopPropagation()}
              >
                <div className="notice-modal-content">
                  <div className="notice-modal-title">공지 설정</div>
                  <textarea
                    className="notice-modal-input"
                    value={tempNoticeText}
                    onChange={(e) => setTempNoticeText(e.target.value)}
                    placeholder="공지를 입력해주세요."
                    autoFocus
                    onFocus={(e) => {
                      const len = e.target.value.length;
                      e.target.setSelectionRange(len, len);
                    }}
                  />
                </div>
                <div className="notice-modal-buttons">
                  <button
                    className="notice-modal-btn notice-modal-btn-cancel"
                    onClick={() => setIsNoticeModalOpen(false)}
                  >
                    취소
                  </button>
                  <button
                    className="notice-modal-btn notice-modal-btn-save"
                    onClick={async () => {
                      setNoticeText(prev => ({
                        ...prev,
                        [selectedLanguage]: tempNoticeText
                      }));
                      setIsNoticeModalOpen(false);

                      const storeId = getStoreIdFromToken() || 1;
                      const updatedTexts = {
                        ...noticeText,
                        [selectedLanguage]: tempNoticeText
                      };

                      try {
                        const response = await fetch(`/api/notices/${storeId}`, {
                          method: 'POST',
                          headers: {
                            'Content-Type': 'application/json',
                            ...authHeaders()
                          },
                          body: JSON.stringify({
                            texts: updatedTexts,
                            enabled: isNoticeEnabled
                          })
                        });
                        const data = await response.json();
                        if (data.success) {
                          localStorage.setItem('noticeUpdated', Date.now().toString());
                          window.dispatchEvent(new StorageEvent('storage', {
                            key: 'noticeUpdated',
                            newValue: Date.now().toString(),
                            url: window.location.href
                          }));
                          showToast('<svg width="16" height="12" viewBox="0 0 16 12" fill="none" xmlns="http://www.w3.org/2000/svg" style="display: inline-block; vertical-align: middle; margin-right: 6px;"><path d="M5.75416 8.8875L13.5229 1.11875C13.7062 0.935417 13.9201 0.84375 14.1646 0.84375C14.409 0.84375 14.6229 0.935417 14.8062 1.11875C14.9896 1.30208 15.0812 1.51994 15.0812 1.77233C15.0812 2.02472 14.9896 2.24228 14.8062 2.425L6.39583 10.8583C6.21249 11.0417 5.9986 11.1333 5.75416 11.1333C5.50971 11.1333 5.29583 11.0417 5.11249 10.8583L1.17083 6.91667C0.987492 6.73333 0.899492 6.51578 0.906826 6.264C0.914159 6.01222 1.0098 5.79436 1.19374 5.61042C1.37769 5.42647 1.59555 5.33481 1.84733 5.33542C2.10033 5.33603 2.32694 5.42958 2.51458 5.61625L5.75416 8.8875Z" fill="#FFD700"/></svg>공지가 수정되었습니다');
                        }
                      } catch (error) {
                        console.error('Error saving notice:', error);
                      }
                    }}
                  >
                    저장
                  </button>
                </div>
              </div>
            </div>
          )}


          {/* Category Container */}
          <div className="category-container" ref={tabsRef}>
            <div className="category-tabs" ref={tabContainerRef}>
              {categories
                .filter(category => {
                  // OKDC/Union/METACITY 매장은 "네이버"·"리뷰" 포함 카테고리 숨김 (리뷰 설정 페이지에서 관리)
                  if (['okdc', 'unionpos', 'union', 'metacity', 'toss'].includes(storeInfo.pos_type)) {
                    if (category.includes('네이버') || category.includes('리뷰')) return false;
                  }
                  // 숨김 처리된 카테고리 필터링
                  if (categoryCodes[`${category}_hidden`]) return false;
                  // POS 모드: 메뉴 0개 카테고리 숨김. 커스텀 모드: 0개여도 표시.
                  if (menuDisplayMode !== 'custom' && !((menuData[category] || []).length > 0)) return false;
                  return true;
                })
                .map((category, index) => (
                <div
                  key={index}
                  ref={el => tabRefs.current[index] = el}
                  className={`category-tab ${activeTab === index ? 'active' : ''}`}
                  onClick={() => handleTabClick(index)}
                >
                  {(() => {
                    const categoryCode = categoryCodes[category];
                    return (categoryCode && categoryTranslations[categoryCode]?.[selectedLanguage]) || category;
                  })()}
                </div>
              ))}
              <div 
                className="category-highlight"
                style={highlightStyle}
              />
            </div>
            {/* 카테고리 수정 버튼 */}
            <button 
              className="category-edit-button"
              onClick={() => setIsCategoryEditOpen(true)}
            >
              <img 
                src={blurBackgroundIcon}
                alt=""
                className="category-edit-blur"
              />
              <img 
                src={categoryModifyIcon}
                alt="카테고리 수정"
                className="category-edit-icon"
              />
            </button>
          </div>

          {/* Menu Containers */}
          <div className="menu-display-container">
            {categories
              .filter(category => {
                // OKDC/Union/METACITY 매장은 "네이버"·"리뷰" 포함 카테고리 숨김 (리뷰 설정 페이지에서 관리)
                if (['okdc', 'unionpos', 'union', 'metacity', 'toss'].includes(storeInfo.pos_type)) {
                  if (category.includes('네이버') || category.includes('리뷰')) return false;
                }
                // 숨김 처리된 카테고리 필터링
                if (categoryCodes[`${category}_hidden`]) return false;
                return true;
              })
              .map((category, categoryIndex) => (
              <div
                key={category}
                className={`menu-container ${isDraggingMenu && draggedMenuInfo?.category === category ? 'dragging' : ''}`}
                ref={el => sectionRefs.current[categoryIndex] = el}
              >
                <div
                  className="category-badge-container"
                  ref={el => categoryHeaderRefs.current[categoryIndex] = el}
                  style={(menuData[category] || []).length === 0 ? { paddingBottom: '22px' } : undefined}
                >
                  <div className="category-badge">
                    <span className="category-badge-name">
                      {(() => {
                        const categoryCode = categoryCodes[category];
                        return (categoryCode && categoryTranslations[categoryCode]?.[selectedLanguage]) || category;
                      })()}
                    </span>
                  </div>

                  {/* + 추가 버튼 — WowPos = 새 메뉴 직접, OKDC/Union(mode2) = 픽커 */}
                  {(storeInfo.pos_type === 'wowpos' || menuDisplayMode === 'custom') && (
                    <button
                      type="button"
                      className="category-add-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (navigator.vibrate) navigator.vibrate(30);
                        setAddMenuCategoryName(category);
                        setAddMenuMode(storeInfo.pos_type === 'wowpos' ? 'wowpos' : 'picker');
                        setIsAddMenuOpen(true);
                      }}
                      aria-label="메뉴 추가"
                    >+</button>
                  )}
                </div>
                {(menuData[category] || []).filter(item => !item.is_hidden).map((item, itemIndex) => (
                  <div
                    key={item.id || itemIndex}
                    ref={(el) => setMenuItemRef(category, itemIndex, el)}
                    data-menu-id={item.id || item.db_id}
                    data-prod-cd={item.prod_cd}
                    className={`menu-item ${item.soldOut ? 'sold-out' : ''} ${calculateTimeRemaining(item) ? 'time-limited' : ''} ${isDraggingMenu ? 'dragging-mode' : ''} ${!(item.image_url || item.image) ? 'no-image' : ''}`}
                    style={{ cursor: 'pointer' }}
                    onTouchStart={(e) => handleMenuTouchStart(e, category, itemIndex, item)}
                    onTouchEnd={handleMenuTouchEnd}
                    onMouseDown={(e) => handleMenuTouchStart(e, category, itemIndex, item)}
                    onMouseUp={handleMenuTouchEnd}
                    onClick={(e) => {
                      // 드래그 중이면 click 무시 (long-press → drag 시 카드 편집 안 열림)
                      if (isDraggingMenu || isLongPressing) {
                        e.preventDefault();
                        e.stopPropagation();
                        return;
                      }

                      // 리플 효과 생성 (단순 클릭 시만)
                      createRipple(e.currentTarget);

                      e.stopPropagation();
                      setSelectedMenuForEdit(item);
                      setIsMenuEditOpen(true);
                    }}
                  >
                    <div 
                      className="menu-item-basic"
                    >
                      <div className="menu-info">
                        <div className="menu-tags">
                          {(() => {
                            const tags = [];
                            if (item.is_popular || item.tags?.includes('popular')) tags.push('popular');
                            if (item.is_recommend || item.tags?.includes('recommend') || item.tags?.includes('recommended')) tags.push('recommend');
                            if (item.is_new || item.tags?.includes('new')) tags.push('new');
                            if (item.is_most_ordered) tags.push('mostOrdered');

                            return tags.sort((a, b) => {
                              const order = { 'popular': 0, 'recommend': 1, 'new': 2, 'mostOrdered': 3 };
                              return order[a] - order[b];
                            }).map((tag, tagIndex) => {
                              if (tag === 'mostOrdered') {
                                const mostOrderedLabel = {
                                  ko: '많이 골랐어요!',
                                  en: 'Most Ordered!',
                                  ja: '人気No.1!',
                                  zh: '人气推荐!',
                                }[selectedLanguage] || '많이 골랐어요!';
                                return (
                                  <span key={tagIndex} className="menu-tag-most-ordered">{mostOrderedLabel}</span>
                                );
                              }
                              const getTagImageUrl = () => {
                                const langPath = selectedLanguage === 'en' ? 'english' : selectedLanguage === 'ja' ? 'japan' : selectedLanguage === 'zh' ? 'china' : 'korean';
                                const tagName = tag === 'popular' ? 'popular' : tag === 'recommend' ? 'recommend' : 'new';
                                return menutagMap[langPath]?.[tagName];
                              };

                              return (
                                <img
                                  key={tagIndex}
                                  className="menu-tag"
                                  src={getTagImageUrl()}
                                  alt={tag}
                                />
                              );
                            });
                          })()}
                        </div>
                        <div className="menu-name">{item[selectedLanguage]?.name || item.ko?.name || item.name}</div>
                        <div className="menu-description">
                          {item[selectedLanguage]?.description !== undefined && item[selectedLanguage]?.description !== null
                            ? item[selectedLanguage].description
                            : (item.ko?.description !== undefined && item.ko?.description !== null
                              ? item.ko.description
                              : '')
                          }
                        </div>
                        <div className="menu-price">
                          {item[selectedLanguage]?.price || item.ko?.price || item.price}
                        </div>
                      </div>
                      {/* 이미지가 있거나, 뱃지(품절/시간제한)가 있을 때만 컨테이너 표시 */}
                      {((item.image_url || item.image) ||
                        (item.soldOut || item.is_soldout) ||
                        calculateTimeRemaining(item)) && (
                        <div className={`admin-menu-image-container ${!(item.image_url || item.image) ? 'no-image' : ''}`}>
                        {(item.image_url || item.image) ? (
                          <img
                            className="admin-menu-image"
                            src={convertToCdnUrl(item.image_url || item.image)}
                            alt={item[selectedLanguage]?.name || item.ko?.name || item.name}
                            onContextMenu={(e) => e.preventDefault()}
                          />
                        ) : null}
                        {(item.soldOut || item.is_soldout) && !calculateTimeRemaining(item) && (
                          <div className="sold-out-overlay">
                            <div className="sold-out-badge">
                              {selectedLanguage === 'ko' ? '품절' :
                               selectedLanguage === 'en' ? 'Sold out' :
                               selectedLanguage === 'ja' ? '売切れ' :
                               '售완'}
                            </div>
                          </div>
                        )}
                        {(() => {
                          // 판매시간 우선 — 판매시간 밖이면 품절이어도 오픈 시간 배지를 먼저 표시
                          const timeInfo = calculateTimeRemaining(item);
                          if (timeInfo) {
                            return (
                              <div className="sold-out-overlay">
                                <div className="time-limit-badge">
                                  {(() => {
                                    if (timeInfo.hours !== null) {
                                      // 1시간 이상 남았을 때
                                      return selectedLanguage === 'ko' 
                                        ? `${timeInfo.hours}시간 후 오픈`
                                        : selectedLanguage === 'en'
                                        ? `Opens in ${timeInfo.hours}h`
                                        : selectedLanguage === 'ja'
                                        ? `${timeInfo.hours}時間後オープン`
                                        : `${timeInfo.hours}小时后开放`;
                                    } else if (timeInfo.minutes !== null) {
                                      // 1시간 미만 남았을 때
                                      return selectedLanguage === 'ko' 
                                        ? `${timeInfo.minutes}분 후 오픈`
                                        : selectedLanguage === 'en'
                                        ? `Opens in ${timeInfo.minutes}m`
                                        : selectedLanguage === 'ja'
                                        ? `${timeInfo.minutes}分後オープン`
                                        : `${timeInfo.minutes}分钟后开放`;
                                    }
                                  })()}
                                </div>
                              </div>
                            );
                          }
                          return null;
                        })()}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
        
        {/* White Spacer Box */}

        {/* Bottom Info */}
        <div className="bottom-info">
          <div className="info-section">
            <div className="info-title">{t.businessInfo}</div>
            <div className="info-content">
              {(!storeInfo.businessName && !storeInfo.address && !storeInfo.phoneNumber && !storeInfo.businessNumber) ? (
                '사업자 정보가 없습니다'
              ) : (
                <>
                  {storeInfo.businessName && <>상호명 : {storeInfo.businessName}<br /></>}
                  {storeInfo.address && <>주소 : {storeInfo.address}<br /></>}
                  {storeInfo.phoneNumber && <>전화번호 : {storeInfo.phoneNumber}<br /></>}
                  {storeInfo.businessNumber && <>사업자등록번호 : {storeInfo.businessNumber}</>}
                </>
              )}
            </div>
          </div>

          <div className="info-section">
            <div className="info-title">{t.originInfo}</div>
            <div className="info-content">
              {storeInfo.origins || '원산지 정보가 없습니다'}
            </div>
          </div>
        </div>

        {/* Fixed Bottom Button - Removed for admin page */}
        
        {/* Toast popup */}
        {showSaveToast && (
          <div
            className="toast-popup"
            dangerouslySetInnerHTML={{
              __html: saveToastError
                ? `<img src="${refuseIcon}" style="width: 12px; height: 12px; vertical-align: middle; margin-right: 6px;" alt="x"/>${saveToastError}`
                : '<svg width="16" height="12" viewBox="0 0 16 12" fill="none" xmlns="http://www.w3.org/2000/svg" style="display: inline-block; vertical-align: middle; margin-right: 6px;"><path d="M5.75416 8.8875L13.5229 1.11875C13.7062 0.935417 13.9201 0.84375 14.1646 0.84375C14.409 0.84375 14.6229 0.935417 14.8062 1.11875C14.9896 1.30208 15.0812 1.51994 15.0812 1.77233C15.0812 2.02472 14.9896 2.24228 14.8062 2.425L6.39583 10.8583C6.21249 11.0417 5.9986 11.1333 5.75416 11.1333C5.50971 11.1333 5.29583 11.0417 5.11249 10.8583L1.17083 6.91667C0.987492 6.73333 0.899492 6.51578 0.906826 6.264C0.914159 6.01222 1.0098 5.79436 1.19374 5.61042C1.37769 5.42647 1.59555 5.33481 1.84733 5.33542C2.10033 5.33603 2.32694 5.42958 2.51458 5.61625L5.75416 8.8875Z" fill="#FFD700"/></svg>매장 정보가 저장됐어요!'
            }}
          />
        )}
        
        {/* Menu Toast popup */}
        {showMenuToast && (
          <div 
            className="toast-popup" 
            dangerouslySetInnerHTML={{ 
              __html: `<svg width="16" height="12" viewBox="0 0 16 12" fill="none" xmlns="http://www.w3.org/2000/svg" style="display: inline-block; vertical-align: middle; margin-right: 6px;"><path d="M5.75416 8.8875L13.5229 1.11875C13.7062 0.935417 13.9201 0.84375 14.1646 0.84375C14.409 0.84375 14.6229 0.935417 14.8062 1.11875C14.9896 1.30208 15.0812 1.51994 15.0812 1.77233C15.0812 2.02472 14.9896 2.24228 14.8062 2.425L6.39583 10.8583C6.21249 11.0417 5.9986 11.1333 5.75416 11.1333C5.50971 11.1333 5.29583 11.0417 5.11249 10.8583L1.17083 6.91667C0.987492 6.73333 0.899492 6.51578 0.906826 6.264C0.914159 6.01222 1.0098 5.79436 1.19374 5.61042C1.37769 5.42647 1.59555 5.33481 1.84733 5.33542C2.10033 5.33603 2.32694 5.42958 2.51458 5.61625L5.75416 8.8875Z" fill="#FFD700"/></svg>${menuToastMessage}` 
            }} 
          />
        )}
        
        {/* Review Settings Toast popup */}
        {showReviewToast && (
          <div
            className="toast-popup"
            dangerouslySetInnerHTML={{
              __html: '<svg width="16" height="12" viewBox="0 0 16 12" fill="none" xmlns="http://www.w3.org/2000/svg" style="display: inline-block; vertical-align: middle; margin-right: 6px;"><path d="M5.75416 8.8875L13.5229 1.11875C13.7062 0.935417 13.9201 0.84375 14.1646 0.84375C14.409 0.84375 14.6229 0.935417 14.8062 1.11875C14.9896 1.30208 15.0812 1.51994 15.0812 1.77233C15.0812 2.02472 14.9896 2.24228 14.8062 2.425L6.39583 10.8583C6.21249 11.0417 5.9986 11.1333 5.75416 11.1333C5.50971 11.1333 5.29583 11.0417 5.11249 10.8583L1.17083 6.91667C0.987492 6.73333 0.899492 6.51578 0.906826 6.264C0.914159 6.01222 1.0098 5.79436 1.19374 5.61042C1.37769 5.42647 1.59555 5.33481 1.84733 5.33542C2.10033 5.33603 2.32694 5.42958 2.51458 5.61625L5.75416 8.8875Z" fill="#FFD700"/></svg>저장되었습니다!'
            }}
          />
        )}

        {/* 메인 토스트 (showToast 헬퍼) — scale-wrapper 안에서 다른 토스트와 동일 zoom으로 표시 */}
        {isToastVisible && (
          <div
            className="toast-popup"
            dangerouslySetInnerHTML={{ __html: toastMessage }}
          />
        )}
      </div>
      
      {/* 메뉴 옵션 바텀시트 - Removed for admin page */}
      
      {/* 장바구니 화면 - Removed for admin page */}
      
      {/* 주문 내역 화면 - Removed for admin page */}
      
      {/* 직원호출 화면 - Removed for admin page */}
      
      {/* 메뉴 추가/수정 바텀시트 */}
      <MenuEditBottomSheet
        isOpen={isMenuEditOpen}
        onClose={handleMenuEditClose}
        selectedLanguage={selectedLanguage}
        menuItem={selectedMenuForEdit}
        category={editingCategory}
        storeInfo={storeInfo}
        onRequestBulkSalesTime={(st) => { setBulkSalesTimeValue(st); setBulkSalesTimeOpen(true); }}
        onRequestBulkSoldOut={(so) => { setBulkSoldOutValue(so); setBulkSoldOutOpen(true); }}
        onUpdate={(updatedMenuItem) => {
          // 메뉴 데이터 즉시 업데이트
          console.log('메뉴 업데이트:', updatedMenuItem);
          setMenuData(prevMenuData => {
            const updatedMenuData = {};

            // 모든 카테고리를 순회하면서 깊은 복사와 함께 업데이트
            Object.keys(prevMenuData).forEach(cat => {
              updatedMenuData[cat] = prevMenuData[cat].map(item => {
                if (item.id === updatedMenuItem.id) {
                  // 해당 메뉴를 완전히 새로운 객체로 교체
                  return updatedMenuItem;
                }
                return item;
              });
            });

            console.log('Updated menuData:', updatedMenuData);
            return updatedMenuData;
          });

          // 선택된 메뉴도 업데이트
          if (selectedMenuForEdit && selectedMenuForEdit.id === updatedMenuItem.id) {
            setSelectedMenuForEdit(updatedMenuItem);
          }
        }}
        onDeleteMenu={(category, menuId, shouldCloseSheet = true) => {
          // console.log('메뉴 삭제 - category:', category, 'menuId:', menuId);
          
          // 메뉴 삭제
          setMenuData(prevMenuData => {
            const updatedMenuData = { ...prevMenuData };
            if (updatedMenuData[category]) {
              updatedMenuData[category] = updatedMenuData[category].filter(item => item.id !== menuId);
            }
            
            // 같은 탭의 다른 컴포넌트에 알림
            window.dispatchEvent(new CustomEvent('menuDataUpdated', {
              detail: { menuData: updatedMenuData }
            }));
            
            // console.log('메뉴 삭제 후 updatedMenuData:', updatedMenuData);
            return updatedMenuData;
          });
          
          // shouldCloseSheet가 true일 때만 바텀시트 닫기
          if (shouldCloseSheet) {
            setIsMenuEditOpen(false);
            setSelectedMenuForEdit(null);
          }
        }}
        onAddMenu={(category, newMenuItem) => {
          // console.log('onAddMenu 호출됨 - category:', category);
          // console.log('onAddMenu 호출됨 - newMenuItem:', newMenuItem);
          // console.log('현재 menuData:', menuData);
          // console.log('수정 모드:', !!selectedMenuForEdit);
          
          // 메뉴 데이터에 새 메뉴 추가 또는 기존 메뉴 수정 (불변성 유지)
          setMenuData(prevMenuData => {
            const updatedMenuData = { ...prevMenuData };
            if (!updatedMenuData[category]) {
              updatedMenuData[category] = [];
            }
            
            if (selectedMenuForEdit) {
              // 수정 모드: 기존 메뉴 업데이트
              updatedMenuData[category] = updatedMenuData[category].map(item => {
                if (item.id === newMenuItem.id) {
                  // 기존 아이템과 비교하여 변경사항 추적
                  const updatedItem = { ...newMenuItem };
                  
                  // 기존 userModified 플래그 유지하면서 새로운 수정사항 추가
                  if (!updatedItem.userModified) {
                    updatedItem.userModified = item.userModified || {};
                  } else {
                    // 기존 플래그와 병합
                    updatedItem.userModified = { ...item.userModified, ...updatedItem.userModified };
                  }
                  
                  // 이름 변경 확인 (ItemCode가 있는 Union 메뉴인 경우만)
                  if (item.itemCode && item.ko?.name !== newMenuItem.ko?.name) {
                    updatedItem.userModified.name = true;
                    console.log(`메뉴 이름 수정됨: ${item.ko?.name} → ${newMenuItem.ko?.name} (ItemCode: ${item.itemCode})`);
                  }
                  
                  // 설명은 Union API에서 제공하지 않으므로 userModified 플래그 추가하지 않음
                  // 설명은 항상 관리자가 설정한 값이 유지됨
                  
                  // 가격 변경 확인 (고정세트는 제외)
                  if (item.itemCode && item.ko?.price !== newMenuItem.ko?.price) {
                    // 고정세트는 가격 수정 플래그를 설정하지 않음 (항상 API 가격 사용)
                    if (item.menuType !== '고정세트') {
                      updatedItem.userModified.price = true;
                      console.log(`메뉴 가격 수정됨: ${item.ko?.name} (ItemCode: ${item.itemCode})`);
                    } else {
                      console.log(`고정세트 가격은 수정 불가: ${item.ko?.name} (API 가격 사용)`);
                    }
                  }
                  
                  return updatedItem;
                }
                return item;
              });
            } else {
              // 추가 모드: 새 메뉴 추가
              updatedMenuData[category] = [...(updatedMenuData[category] || []), newMenuItem];
            }
            
            // localStorage 사용 중단 - DB만 사용
            // localStorage.setItem('menuData', JSON.stringify(updatedMenuData));
            
            // console.log('메뉴 처리 후 updatedMenuData:', updatedMenuData);
            // console.log('카테고리별 메뉴 개수:', updatedMenuData[category]?.length);
            
            return updatedMenuData;
          });
          
          // 토스트 메시지 표시
          if (selectedMenuForEdit) {
            setMenuToastMessage('메뉴가 수정되었어요!');
          } else {
            setMenuToastMessage('메뉴가 추가되었어요!');
          }
          setShowMenuToast(true);
          
          // 2초 후 토스트 숨기기
          setTimeout(() => {
            setShowMenuToast(false);
          }, 2000);
          
          // 메뉴 추가/수정 완료 후 초기화
          setTimeout(() => {
            setEditingCategory('');
            setSelectedMenuForEdit(null);
          }, 300);
        }}
        onShowToast={showToast}
      />

      {/* 판매시간 일괄 적용 모달 — 메뉴 편집 저장(판매시간 변경) 시 노출 */}
      <SalesTimeBulkApply
        isOpen={bulkSalesTimeOpen}
        onClose={() => setBulkSalesTimeOpen(false)}
        onApplied={() => fetchMenusFromDB(true)}
        storeId={storeInfo.id}
        salesTime={bulkSalesTimeValue}
      />

      <SoldOutBulkApply
        isOpen={bulkSoldOutOpen}
        onClose={() => setBulkSoldOutOpen(false)}
        onApplied={() => fetchMenusFromDB(true)}
        storeId={storeInfo.id}
        soldOut={bulkSoldOutValue}
      />

      {/* 카테고리 편집 바텀시트 */}
      <CategoryEditBottomSheet
        isOpen={isCategoryEditOpen}
        onClose={handleCategoryEditClose}
        onShowToast={showToast}
        selectedLanguage={selectedLanguage}
        categories={categories}
        categoryCodes={categoryCodes}
        categoryTranslations={categoryTranslations}
        mode={menuDisplayMode}
        storeId={storeInfo.id}
        posType={storeInfo.pos_type}
        onCustomChange={() => {
          // 커스텀 카테고리 변경(이름/추가/삭제/순서) 시 카테고리 번역 localStorage 캐시 무효화
          // (안 지우면 5분 캐시가 옛 이름을 화면에 계속 덮어씀 — Ctrl+F5로도 안 지워짐)
          const sid = storeInfo.id || getStoreIdFromToken();
          if (sid) {
            localStorage.removeItem(`categoryTranslations_${sid}`);
            localStorage.removeItem(`categoryTranslations_timestamp_${sid}`);
          }
          setCategoryTranslations({});
          fetchMenusFromDB(true);
        }}
        onUpdateCategoryTranslation={async (originalKey, language, newName) => {
          console.log('=== onUpdateCategoryTranslation 호출 ===');
          console.log('originalKey (카테고리명):', originalKey);
          console.log('language:', language);
          console.log('newName:', newName);

          // 한국어는 수정 불가
          if (language === 'ko') {
            console.log('한국어 카테고리명은 수정할 수 없습니다.');
            return;
          }

          // originalKey가 없거나 빈 문자열이면 처리 중단
          if (!originalKey || originalKey.trim() === '') {
            console.warn('originalKey가 없거나 빈 문자열입니다. 카테고리 업데이트를 건너뜁니다.');
            console.warn('originalKey 값:', originalKey);
            return;
          }

          // 카테고리명으로 카테고리 코드 찾기
          const categoryCode = categoryCodes[originalKey];
          if (!categoryCode) {
            console.warn('카테고리 코드를 찾을 수 없습니다:', originalKey);
            return;
          }

          console.log('카테고리 코드:', categoryCode);

          try {
            // 현재 카테고리의 모든 언어 번역 가져오기
            const currentTranslations = categoryTranslations[categoryCode] || {
              ko: newName,
              en: newName,
              ja: newName,
              zh: newName
            };

            console.log('현재 번역 정보:', currentTranslations);

            // 선택된 언어만 업데이트
            currentTranslations[language] = newName;

            console.log('업데이트된 번역 정보:', currentTranslations);

            // Update category translation in DB
            const storeId = getStoreIdFromToken() || 1; // Get actual store ID from token
            const apiUrl = `/api/translations/category/${storeId}/${encodeURIComponent(categoryCode)}`;
            console.log('API 호출:', apiUrl);
            console.log('요청 body:', {
              language,
              name: newName
            });

            const response = await fetch(apiUrl, {
              method: 'PUT',
              headers: {
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                language: language,
                name: newName
              })
            });

            console.log('API 응답 상태:', response.status);
            const data = await response.json();
            console.log('API 응답 데이터:', data);

            if (data.success) {
              // Update local state only after successful DB update
              const updatedTranslations = {
                ...categoryTranslations,
                [categoryCode]: {  // categoryCode를 키로 사용
                  ...categoryTranslations[categoryCode],
                  [language]: newName
                }
              };
              setCategoryTranslations(updatedTranslations);

              // 캐시 무효화
              const storeId = getStoreIdFromToken() || 1;
              const cacheKey = `categoryTranslations_${storeId}`;
              const cacheTimestampKey = `categoryTranslations_timestamp_${storeId}`;

              // 기존 캐시 삭제
              localStorage.removeItem(cacheKey);
              localStorage.removeItem(cacheTimestampKey);

              // 손님 페이지 새로고침 유도
              localStorage.setItem('categoryTranslationsUpdated', Date.now().toString());

              console.log('카테고리 번역 업데이트 성공 및 캐시 무효화');
              console.log('업데이트된 번역:', updatedTranslations);
              console.log('=== onUpdateCategoryTranslation 완료 ===');
            } else {
              console.error('카테고리 번역 업데이트 실패:', data.message);
              alert('카테고리 번역 업데이트 실패: ' + data.message);
            }
          } catch (error) {
            console.error('카테고리 번역 업데이트 중 오류:', error);
            alert('카테고리 번역 업데이트 중 오류가 발생했습니다.');
          }
        }}
        onReorder={async (newCategories) => {
          console.log('=== onReorder 호출 ===');
          console.log('newCategories:', newCategories);
          console.log('현재 categoryTranslations:', categoryTranslations);

          const updatedMenuData = {};

          // DB에 카테고리 순서 업데이트 (객체 배열 그대로 전달)
          try {
            await api.updateCategoryOrder(newCategories);
            console.log('카테고리 순서가 DB에 업데이트되었습니다.');

            // 모든 localStorage 캐시 무효화
            const storeId = getStoreIdFromToken() || 1;
            localStorage.removeItem(`categoryTranslations_${storeId}`);
            localStorage.removeItem(`categoryTranslations_timestamp_${storeId}`);
            localStorage.removeItem('menuData');
            localStorage.removeItem('categories');
            localStorage.removeItem('categoriesWithCodes');

            // 이벤트 발생으로 다른 탭/페이지에 알림
            localStorage.setItem('categoryOrderUpdated', Date.now().toString());
            localStorage.setItem('categoryTranslationsUpdated', Date.now().toString());

            // DB에서 최신 데이터 로드 (Redis 캐시 바이패스)
            await fetchMenusFromDB(true); // refresh=true로 캐시 바이패스
            console.log('메뉴 데이터를 DB에서 직접 로드했습니다 (캐시 바이패스).');

            // 카테고리 상태 업데이트는 fetchMenusFromDB 내에서 처리됨
          } catch (error) {
            console.error('카테고리 순서 업데이트 실패:', error);
          }

          // 새 카테고리의 번역 정보 저장
          const categoryKeys = newCategories.map(c => typeof c === 'string' ? c : c.name);
          for (const categoryKey of categoryKeys) {
            // 기존 카테고리가 아닌 경우 (새 카테고리)
            if (!categoryTranslations[categoryKey]) {
              console.log('새 카테고리 감지:', categoryKey);

              // 새 카테고리의 기본 번역 정보 생성 (모든 언어에 동일한 이름 사용)
              const newTranslations = {
                ko: categoryKey,
                en: categoryKey,
                ja: categoryKey,
                zh: categoryKey
              };

              // 로컬 상태에만 저장 (DB 저장은 별도 API가 필요)
              setCategoryTranslations(prev => ({
                ...prev,
                [categoryKey]: newTranslations
              }));

              console.log('새 카테고리 로컬 저장됨:', categoryKey);

              // TODO: 새 카테고리를 DB에 저장하는 별도 API 필요
              // 현재는 로컬 상태에만 저장되고, 실제 DB 저장은 되지 않음
            }
          }

          // 메뉴 데이터는 카테고리 이름과 함께 유지 (순서만 변경)
          // 카테고리 순서가 바뀌어도 각 카테고리의 메뉴는 그대로 유지
          const categoryNames = newCategories.map(c => typeof c === 'string' ? c : c.name);
          categoryNames.forEach((categoryName) => {
            // 각 카테고리의 메뉴 데이터를 그대로 유지
            if (menuData[categoryName]) {
              updatedMenuData[categoryName] = menuData[categoryName];
            } else {
              // 새로 추가된 카테고리인 경우만 빈 배열
              updatedMenuData[categoryName] = [];
            }
          });

          // 삭제된 카테고리는 제외 (newCategories에 없는 카테고리)

          setMenuData(updatedMenuData);
          setCategories(categoryNames);
          // console.log('카테고리 변경:', newCategories);
        }}
      />
      
      {/* 호출 설정 바텀시트 */}
      <CallSettingsBottomSheet
        isOpen={isCallSettingsOpen}
        onClose={handleCallSettingsClose}
        selectedLanguage={selectedLanguage}
        callReasons={callReasons}
        onShowToast={showToast}
        posType={storeInfo.pos_type}
        onEditItem={async (reasonId, newText, language) => {
          // 특정 호출 사유의 텍스트 업데이트
          try {
            await api.updateCallReason(reasonId, language, newText);
            console.log('호출 사유가 수정되었습니다:', reasonId, newText);

            // 목록 새로고침
            const response = await api.getCallReasons(selectedLanguage);
            if (response.success && response.reasons) {
              const reasonsData = response.reasons.map(r => ({
                id: r.id,
                text: r.reason_text,
                originalText: r.original_text
              }));
              setCallReasons(reasonsData);
            }
          } catch (error) {
            console.error('호출 사유 수정 실패:', error);
          }
        }}
        onReorder={async (newCallReasons) => {
          // 화면에 표시된 순서 그대로 모두 저장
          console.log('Saving call reasons in exact order:', newCallReasons);
          console.log('Current language:', selectedLanguage);

          try {
            // 백엔드에 전체 목록을 전송 (화면 순서 그대로 + 현재 언어)
            const response = await api.updateCallReasonsOrder(newCallReasons, selectedLanguage);

            if (response.success) {
              console.log('호출 사유가 업데이트되었습니다.');

              // 백엔드가 반환한 업데이트된 목록으로 상태 업데이트
              if (response.reasons) {
                const updatedReasons = response.reasons.map(r => ({
                  id: r.id,
                  text: r.reason_text,
                  originalText: r.reason_text,
                  display_order: r.display_order
                }));
                setCallReasons(updatedReasons);
              } else {
                // 백엔드가 목록을 반환하지 않으면 현재 목록 유지
                setCallReasons(newCallReasons);
              }
            }
          } catch (error) {
            console.error('호출 사유 업데이트 실패:', error);
            // 실패 시 목록 새로고침
            const response = await api.getCallReasons(selectedLanguage);
            if (response.success && response.reasons) {
              const reasonsData = response.reasons.map(r => ({
                id: r.id,
                text: r.reason_text,
                originalText: r.original_text || r.reason_text
              }));
              setCallReasons(reasonsData);
            }
          }
        }}
      />

      {/* 리뷰 설정 페이지 */}
      <ReviewSettings
        isOpen={isReviewSettingsOpen}
        storeId={getStoreIdFromToken() || 1}
        onClose={handleReviewSettingsClose}
        onSave={() => {
          setShowReviewToast(true);
          setTimeout(() => {
            setShowReviewToast(false);
          }, 2000);
        }}
      />

      {/* 메뉴 추가 — WowPos: MenuEditBottomSheet 신규 모드, 저장 시 wowpos_menus 즉시 INSERT.
           조건부 mount 안 함 (슬라이드 닫힘 애니메이션 보존) — isOpen 만 토글 + mode 지연 reset. */}
      {addMenuMode === 'wowpos' && (
        <MenuEditBottomSheet
          isOpen={isAddMenuOpen}
          onClose={() => {
            setIsAddMenuOpen(false);
            // 슬라이드 애니메이션 시간 (보통 300ms) 후 mode 해제 → 컴포넌트 unmount
            setTimeout(() => setAddMenuMode(null), 350);
          }}
          selectedLanguage={selectedLanguage}
          menuItem={null}
          category={addMenuCategoryName}
          storeInfo={{ id: storeInfo.id, pos_type: 'wowpos' }}
          onShowToast={showToast}
          isWowposAdd={true}
          wowposCategoryId={parseInt(String(categoryCodes[addMenuCategoryName] || '').replace('WPC_', ''), 10) || null}
          onUpdate={async (tempMenu) => {
            if (!tempMenu?._wowposTemp) return;
            const catId = parseInt(String(categoryCodes[addMenuCategoryName] || '').replace('WPC_', ''), 10) || null;
            if (!catId) { showToast('카테고리 정보 없음', 'error'); return; }
            try {
              const r = await fetch('/api/wowpos/menus', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...authHeaders() },
                body: JSON.stringify({
                  store_id: storeInfo.id,
                  category_id: catId,
                  name_ko: tempMenu.name_ko || tempMenu.name,
                  description_ko: tempMenu.description_ko,
                  price: tempMenu.price || 0,
                  options: tempMenu.options || [],
                }),
              });
              const d = await r.json();
              if (!d.success) throw new Error(d.error || '메뉴 추가 실패');
              const newId = d.menu?.id;
              // 부가 필드 PUT (값 있을 때만)
              if (newId && (tempMenu.is_popular || tempMenu.is_recommend || tempMenu.is_new || tempMenu.is_most_ordered || tempMenu.is_soldout || tempMenu.is_sales_time)) {
                await fetch(`/api/wowpos/menus/${newId}`, {
                  method: 'PUT',
                  headers: { 'Content-Type': 'application/json', ...authHeaders() },
                  body: JSON.stringify({
                    is_popular: !!tempMenu.is_popular,
                    is_recommend: !!tempMenu.is_recommend,
                    is_new: !!tempMenu.is_new,
                    is_most_ordered: !!tempMenu.is_most_ordered,
                    is_soldout: !!tempMenu.is_soldout,
                    is_sales_time: !!tempMenu.is_sales_time,
                    start_time: tempMenu.is_sales_time ? tempMenu.start_time : null,
                    end_time: tempMenu.is_sales_time ? tempMenu.end_time : null,
                  }),
                });
              }
              // 이미지 업로드
              if (newId && tempMenu._imageFile) {
                const fd = new FormData();
                fd.append('image', tempMenu._imageFile);
                await fetch(`/api/menus/${newId}/image?storeId=${storeInfo.id}`, { method: 'POST', body: fd });
              }
              showToast('메뉴가 추가되었습니다', 'success');
              await fetchMenusFromDB(true);
            } catch (err) {
              console.error('[메뉴 추가] 오류:', err);
              showToast(err.message || '메뉴 추가 실패', 'error');
            }
          }}
        />
      )}

      {/* 메뉴 추가 — OKDC/Union(mode2): MenuPickerBottomSheet, 선택 시 custom_category_items INSERT */}
      {addMenuMode === 'picker' && (
        <MenuPickerBottomSheet
          isOpen={isAddMenuOpen}
          onClose={() => {
            setIsAddMenuOpen(false);
            setTimeout(() => setAddMenuMode(null), 350);
          }}
          storeId={storeInfo.id}
          categoryName={addMenuCategoryName}
          registeredKeys={(menuData[addMenuCategoryName] || []).map(it => it.prod_cd || it.menu_code).filter(Boolean)}
          onSelect={async (menuKey) => {
            const customCatId = parseInt(String(categoryCodes[addMenuCategoryName] || '').replace('custom_', ''), 10) || null;
            if (!customCatId) { showToast('카테고리 정보 없음', 'error'); return; }
            try {
              await fetch(`/api/custom-categories/${customCatId}/items`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...authHeaders() },
                body: JSON.stringify({ menu_key: menuKey })
              });
              await fetchMenusFromDB(true);
            } catch (err) {
              console.error('[메뉴 배치] 오류:', err);
              showToast(err.message || '메뉴 배치 실패', 'error');
            }
          }}
          onDeselect={async (menuKey) => {
            const customCatId = parseInt(String(categoryCodes[addMenuCategoryName] || '').replace('custom_', ''), 10) || null;
            if (!customCatId) return;
            try {
              await fetch(`/api/custom-categories/${customCatId}/items/${encodeURIComponent(menuKey)}`, { method: 'DELETE', headers: authHeaders() });
              await fetchMenusFromDB(true);
            } catch (_) {}
          }}
        />
      )}

      {/* (제거됨 Step 4) MenuReorderBottomSheet — 메뉴 순서 변경 / 추가 / 삭제는
           홈 화면에서 직접 처리 (Step 1: + 추가 버튼, Step 2: 삭제 버튼, Step 3: long-press drag). */}

      {/* 매장 설정 페이지 */}
      {isStoreSettingsOpen && (
        <StoreSettings
          isOpen={isStoreSettingsOpen}
          onClose={handleStoreSettingsClose}
          initialStoreData={storeInfo.pos_type !== undefined ? storeInfo : null}
          onSyncStart={handleSyncStart}
          onSaveSuccess={handleStoreSettingsSave}
          menuDisplayMode={menuDisplayMode}
          isModeSwitching={isModeSwitching}
          onMenuDisplayModeChange={handleModeChange}
          businessOpenTime={businessOpenTime}
          businessCloseTime={businessCloseTime}
          onBusinessHoursChange={handleBusinessHoursChange}
        />
      )}

      {/* 주문내역 총괄 페이지 */}
      {isOrderOverviewOpen && (
        <OrderOverview
          isOpen={isOrderOverviewOpen}
          onClose={handleOrderOverviewClose}
          storeId={storeInfo.store_id}
          posType={storeInfo.pos_type}
        />
      )}
      
      {/* 메뉴 추가 바텀시트 - 이미지 슬라이더용 */}
      <MenuAddBottomSheet
        isOpen={isMenuAddOpen}
        onClose={handleMenuAddClose}
        selectedLanguage={selectedLanguage}
        images={sliderImages}
        onSave={async (finalImages) => {
          // 저장 버튼 클릭 시 처리
          console.log('이미지 저장:', finalImages);

          // 기존 이미지와 비교하여 삭제된 항목 찾기
          const deletedImages = sliderImages.filter(oldImg =>
            !finalImages.some(newImg => newImg.id === oldImg.id)
          );

          // 삭제 처리
          for (const img of deletedImages) {
            if (img && img.id) {
              try {
                const storeId = getStoreIdFromToken() || 1;
                const deleteUrl = `/api/banners/${storeId}/${img.id}`;
                const response = await fetch(deleteUrl, {
                  method: 'DELETE',
                  headers: authHeaders()
                });
                if (response.ok) {
                  console.log('이미지 삭제됨:', img.id);
                }
              } catch (error) {
                console.error('이미지 삭제 실패:', error);
              }
            }
          }

          // 순서 업데이트
          try {
            const reorderedImages = finalImages.map((img, index) => ({
              id: img.id,
              display_order: index + 1
            }));

            const storeId = getStoreIdFromToken() || 1;
            const response = await fetch(`/api/banners/reorder/${storeId}`, {
              method: 'PUT',
              headers: {
                'Content-Type': 'application/json',
                ...authHeaders()
              },
              body: JSON.stringify({ images: reorderedImages })
            });

            if (response.ok) {
              console.log('이미지 순서 저장 완료');
            }
          } catch (error) {
            console.error('이미지 순서 저장 실패:', error);
          }

          // 서버에서 최신 배너 데이터 다시 가져오기
          await fetchBannerImages();
        }}
        onReorder={(newImages) => {
          // 순서 변경 시 UI만 업데이트 (저장 버튼 클릭 시 DB 저장)
          setSliderImages(newImages);
        }}
        onImageUpdate={async (index, newImageUrl, newImageData) => {
          console.log('이미지 업데이트 완료 - index:', index);
          console.log('새 이미지 데이터:', newImageData);

          // 교체 API를 사용했으므로 이미 처리 완료됨
          // 목록만 새로고침
          await fetchBannerImages();
        }}
        onImageDelete={(index) => {
          // UI에서만 제거 (DB 삭제는 저장 버튼 클릭 시)
          console.log('이미지 UI 제거 - index:', index);
          const updatedImages = sliderImages.filter((_, i) => i !== index);
          setSliderImages(updatedImages);
        }}
        onImageAdd={(newImage) => {
          const updatedImages = [...sliderImages, newImage];
          setSliderImages(updatedImages);
          // console.log('이미지 추가:', updatedImages);
        }}
      />

      {/* 토스트 팝업은 scale-wrapper 안으로 이동됨 (이중 zoom으로 다른 토스트와 크기 통일) */}
    </>
  );
};

export default AdminDashboard;